/**
 * Central de Importações de Dados Mestres (ADR 0040): definição de cada cadastro importável.
 *
 * Um motor só (API) lê estas definições para gerar o template oficial (.xlsx com as abas Dados,
 * Instruções, Dicionário de Dados e Listas de Referência), conferir o arquivo enviado e montar a
 * prévia. As regras de negócio continuam nos cadastros de origem (banco e API): o importador grava
 * por eles, com as permissões da pessoa. Para incluir um cadastro novo, basta uma definição aqui
 * e um "importador" na API, sem mudar o motor.
 *
 * Os textos do template ficam em português (layout oficial da LOOT, conferido pela versão); a tela
 * usa os arquivos de tradução.
 */
import { z } from 'zod';
import type { PermissionCode } from './permissions.ts';

export const IMPORT_KIND_IDS = [
  'job_families',
  'job_subfamilies',
  'job_levels',
  'career_tracks',
  'salary_grades',
  'jobs',
  'job_practices',
  'cost_centers',
  'persons',
  'employees',
  'movements',
  'allocations',
  'dp_complement',
  'management_accounts',
  'accounting_accounts',
  'adp_movements',
  'company_bank_accounts',
  'adp_payroll_cost',
  'partners',
  'partner_links',
  'partner_contacts',
] as const;
export type ImportKindId = (typeof IMPORT_KIND_IDS)[number];

export const IMPORT_GROUPS = ['career', 'structure', 'finance', 'people', 'partners'] as const;
export type ImportGroup = (typeof IMPORT_GROUPS)[number];

/** Situações do lote (as mesmas do banco). */
export const IMPORT_BATCH_STATUSES = [
  'received',
  'validating',
  'validated',
  'invalid',
  'ready',
  'processing',
  'done',
  'partial',
  'failed',
  'cancelled',
] as const;
export type ImportBatchStatus = (typeof IMPORT_BATCH_STATUSES)[number];

export const IMPORT_OPERATIONS = ['create', 'update', 'unchanged', 'skip'] as const;
export type ImportOperation = (typeof IMPORT_OPERATIONS)[number];
export const IMPORT_ROW_STATUSES = [
  'valid',
  'warning',
  'invalid',
  'conflict',
  'reconcile',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

/** Limites do arquivo enviado. */
export const IMPORT_MAX_FILE_BYTES = 500 * 1024;
export const IMPORT_MAX_ROWS = 5000;
/** A confirmação vale por este tempo depois da validação. */
export const IMPORT_CONFIRM_MINUTES = 30;
/** Linhas com validação (lista suspensa, data, número) no template. */
export const IMPORT_TEMPLATE_ROWS = IMPORT_MAX_ROWS;

export type ImportColumnType =
  'code' | 'text' | 'integer' | 'decimal' | 'date' | 'email' | 'enum' | 'reference';

export interface ImportColumn {
  /** Nome interno do campo (inglês, estável). */
  key: string;
  /** Cabeçalho exato na aba Dados. */
  header: string;
  description: string;
  type: ImportColumnType;
  /** Tamanho máximo (texto e código). */
  max?: number;
  /** Obrigatório sempre, só ao incluir um registro novo, ou opcional. */
  required: 'always' | 'create' | 'no';
  /** Regra de validação, em linguagem simples (vai para o dicionário). */
  rule: string;
  /** Faz parte do identificador do registro (não muda depois de gravado). */
  key_part?: boolean;
  /** Cadastro de referência e a lista da aba "Listas de Referência" (quando há). */
  reference?: { entity: string; list?: ImportListId };
  /** Valores aceitos (tipo enum): o que aparece na planilha e o valor gravado. */
  values?: readonly { label: string; value: string }[];
  /** Permissão necessária para preencher este campo (além da do cadastro). */
  permission?: PermissionCode;
  /** Campo restrito: nunca sai nas exportações gerais (template de atualização). */
  restricted?: boolean;
  /** Exemplo fictício (aba Instruções). */
  example?: string;
  /** Largura sugerida da coluna no template. */
  width?: number;
}

/** Listas da aba "Listas de Referência" (registros existentes e autorizados para quem baixa). */
export const IMPORT_LISTS = [
  'status',
  'families',
  'subfamilies',
  'subfamily_kinds',
  'levels',
  'tracks',
  'grades',
  'jobs',
  'currencies',
  'legal_entities',
  'departments',
  'cost_centers',
  'cost_center_levels',
  'management_accounts',
  'accounting_accounts',
  'account_natures',
  'account_types',
  'yes_no',
  'employment_types',
  'branches',
  'locations',
  'work_modes',
  'employment_statuses',
  'movement_kinds',
  'marital_statuses',
  'foreign_id_types',
  'partner_categories',
  'banks',
  'bank_account_kinds',
  'bank_account_types',
  'bank_account_purposes',
  'bank_integration_channels',
  'pix_key_types',
] as const;
export type ImportListId = (typeof IMPORT_LISTS)[number];

export const IMPORT_LIST_TITLES: Record<ImportListId, string> = {
  status: 'Situação',
  families: 'Famílias',
  subfamilies: 'Subfamílias',
  subfamily_kinds: 'Tipos de subfamília',
  levels: 'Níveis JL',
  tracks: 'Trilhas',
  grades: 'Grades',
  jobs: 'Cargos',
  currencies: 'Moedas',
  legal_entities: 'Empresas',
  departments: 'Departamentos',
  cost_centers: 'Centros de custo',
  cost_center_levels: 'Níveis dos centros de custo',
  management_accounts: 'Contas gerenciais',
  accounting_accounts: 'Contas contábeis',
  account_natures: 'Naturezas',
  account_types: 'Tipos de conta',
  yes_no: 'Sim/Não',
  employment_types: 'Tipos de vínculo',
  branches: 'Filiais',
  locations: 'Locais',
  work_modes: 'Modalidades',
  employment_statuses: 'Situações funcionais',
  movement_kinds: 'Tipos de movimentação',
  marital_statuses: 'Estados civis',
  foreign_id_types: 'Documentos estrangeiros',
  partner_categories: 'Categorias de parceiro',
  banks: 'Bancos do Brasil',
  bank_account_kinds: 'Tipos de conta bancária',
  bank_account_types: 'Modalidades',
  bank_account_purposes: 'Finalidades',
  bank_integration_channels: 'Canais',
  pix_key_types: 'Tipos de chave PIX',
};

/** Opção do lote informada na tela (vale para as linhas sem o valor). */
export interface ImportOption {
  key: 'defaultValidFrom';
  label: string;
  type: 'date';
  help: string;
}

export interface ImportKind {
  id: ImportKindId;
  group: ImportGroup;
  /** Ordem recomendada de importação (dependências). */
  order: number;
  /** Versão do layout: muda quando colunas mudam (o envio com versão antiga é recusado). */
  version: number;
  /** Nome do cadastro no template (português). */
  title: string;
  /** Como cada linha é identificada. */
  identity: string;
  /** Cadastros que precisam existir antes (desta Central). */
  dependsOn: readonly ImportKindId[];
  /** Cadastros corporativos que precisam existir antes (fora desta Central). */
  prerequisites: readonly string[];
  /** Permissões para gravar (todas). */
  applyPermissions: readonly PermissionCode[];
  /** Permissão para baixar os registros existentes (alguma delas). */
  readPermissions: readonly PermissionCode[];
  /** Como a confirmação grava: tudo ou nada (nenhuma linha com erro). */
  transaction: 'atomic';
  /** Regras de atualização, em linguagem simples (aba Instruções). */
  updateRules: readonly string[];
  columns: readonly ImportColumn[];
  options?: readonly ImportOption[];
}

/** Naturezas do plano gerencial (as mesmas da tela do FP&A). */
export const MANAGEMENT_NATURE_VALUES = [
  { label: 'Receita', value: 'revenue' },
  { label: 'Dedução da receita', value: 'revenue_deduction' },
  { label: 'Custo', value: 'cost' },
  { label: 'Despesa operacional', value: 'operating_expense' },
  { label: 'Outras receitas e despesas operacionais', value: 'other_operating' },
  { label: 'Resultado financeiro', value: 'financial' },
  { label: 'Impostos sobre o lucro', value: 'income_tax' },
  { label: 'Investimento (CAPEX)', value: 'capex' },
  { label: 'Estatística (não monetária)', value: 'statistical' },
] as const;

/** Tipos de conta contábil (os do NetSuite, com os nomes da exportação em português). */
export const ACCOUNTING_TYPE_VALUES = [
  { label: 'Banco', value: 'bank' },
  { label: 'Contas a Receber', value: 'accounts_receivable' },
  { label: 'Outro Ativo Circulante', value: 'other_current_asset' },
  { label: 'Ativo Fixo', value: 'fixed_asset' },
  { label: 'Outro Ativo', value: 'other_asset' },
  { label: 'Contas a Pagar', value: 'accounts_payable' },
  { label: 'Cartão de crédito', value: 'credit_card' },
  { label: 'Outro passivo circulante', value: 'other_current_liability' },
  { label: 'Obrigação de longo prazo', value: 'long_term_liability' },
  { label: 'Patrimônio líquido', value: 'equity' },
  { label: 'Receita', value: 'income' },
  { label: 'Outras receitas', value: 'other_income' },
  { label: 'Custo das mercadorias vendidas', value: 'cost_of_goods_sold' },
  { label: 'Despesa', value: 'expense' },
  { label: 'Outras despesas', value: 'other_expense' },
  { label: 'Despesa diferida', value: 'deferred_expense' },
  { label: 'Sem contabilização', value: 'non_posting' },
] as const;
export type AccountingAccountType = (typeof ACCOUNTING_TYPE_VALUES)[number]['value'];

/** Contas bancárias das empresas (ADR 0073): os mesmos valores da tela. */
const COMPANY_BANK_KIND_VALUES = [
  { label: 'Conta no Brasil', value: 'br_account' },
  { label: 'Conta com IBAN', value: 'iban' },
  { label: 'Conta nos Estados Unidos', value: 'us_account' },
  { label: 'Conta em outro país (SWIFT)', value: 'foreign_account' },
] as const;
const COMPANY_BANK_ACCOUNT_TYPE_VALUES = [
  { label: 'Conta corrente', value: 'checking' },
  { label: 'Poupança', value: 'savings' },
  { label: 'Conta de pagamento', value: 'payment' },
  { label: 'Investimento', value: 'investment' },
  { label: 'Outra', value: 'other' },
] as const;
const COMPANY_BANK_PURPOSE_VALUES = [
  { label: 'Operacional', value: 'operating' },
  { label: 'Recebimentos', value: 'collection' },
  { label: 'Pagamentos', value: 'payments' },
  { label: 'Folha', value: 'payroll' },
  { label: 'Aplicações', value: 'investment' },
  { label: 'Tributos', value: 'tax' },
  { label: 'Câmbio', value: 'fx' },
  { label: 'Cartões', value: 'cards' },
  { label: 'Outra', value: 'other' },
] as const;
const COMPANY_BANK_PIX_TYPE_VALUES = [
  { label: 'CPF', value: 'cpf' },
  { label: 'CNPJ', value: 'cnpj' },
  { label: 'E-mail', value: 'email' },
  { label: 'Telefone', value: 'phone' },
  { label: 'Chave aleatória', value: 'random' },
] as const;
const COMPANY_BANK_CHANNEL_VALUES = [
  { label: 'Digitação', value: 'manual' },
  { label: 'Arquivo do banco (OFX, CNAB)', value: 'file' },
  { label: 'Integração direta (API)', value: 'api' },
  { label: 'Open Finance', value: 'open_finance' },
] as const;

const YES_NO = [
  { label: 'Sim', value: 'yes' },
  { label: 'Não', value: 'no' },
] as const;

const STRUCTURE_WRITE = ['structure.catalog.create', 'structure.catalog.update'] as const;

const WORK_MODE_VALUES = [
  { label: 'Presencial', value: 'onsite' },
  { label: 'Híbrido', value: 'hybrid' },
  { label: 'Remoto', value: 'remote' },
] as const;
const EMPLOYMENT_STATUS_VALUES = [
  { label: 'Ativo', value: 'active' },
  { label: 'Desligado', value: 'terminated' },
  { label: 'Encerrado', value: 'ended' },
] as const;
/** Eventos do relatório da ADP (ADR 0091). */
const ADP_EVENT_VALUES = [
  { label: 'Admissão', value: 'admission' },
  { label: 'Desligamento', value: 'termination' },
] as const;
const MOVEMENT_KIND_VALUES = [
  { label: 'Admissão', value: 'admission' },
  { label: 'Promoção', value: 'promotion' },
  { label: 'Mudança de cargo', value: 'job_change' },
  { label: 'Transferência', value: 'transfer' },
  { label: 'Mudança de área', value: 'department_change' },
  { label: 'Mudança de gestor', value: 'manager_change' },
  { label: 'Mudança de centro de custo', value: 'cost_center_change' },
  { label: 'Mudança de subfamília de atuação', value: 'subfamily_change' },
  { label: 'Desligamento', value: 'termination' },
  { label: 'Encerramento', value: 'end' },
  { label: 'Outro', value: 'other' },
] as const;
const MARITAL_VALUES = [
  { label: 'Solteiro(a)', value: 'single' },
  { label: 'Casado(a)', value: 'married' },
  { label: 'União estável', value: 'stable_union' },
  { label: 'Separado(a)', value: 'separated' },
  { label: 'Divorciado(a)', value: 'divorced' },
  { label: 'Viúvo(a)', value: 'widowed' },
  { label: 'Outro', value: 'other' },
] as const;
const FOREIGN_ID_VALUES = [
  { label: 'Passaporte', value: 'passport' },
  { label: 'RNM', value: 'rnm' },
  { label: 'Outro', value: 'other' },
] as const;

const PEOPLE_READ = [
  'people.person.manage',
  'people.employee.manage',
  'people.allocation.manage',
  'people.private.read',
  'people.private.manage',
] as const;

/** Colunas que identificam o colaborador (ID interno, ou empresa + matrícula). */
/**
 * Identificação do colaborador: o ID interno ou, sem ele, empresa + matrícula. As colunas são
 * alternativas (nenhuma é obrigatória sozinha); o importador recusa a linha sem identificação.
 */
const employeeRef = (required: boolean): ImportColumn[] => [
  {
    key: 'employeeId',
    header: 'Colaborador: ID interno',
    description:
      'Identificador do colaborador (vem no template de atualização de Colaboradores). Vazio = empresa + matrícula.',
    type: 'code',
    max: 36,
    required: 'no',
    rule: 'Não altere: identifica o vínculo',
    example: '',
    width: 38,
  },
  {
    key: 'legalEntity',
    header: 'Empresa (código)',
    description: 'Empresa do vínculo (com a matrícula, identifica o colaborador).',
    type: 'reference',
    max: 40,
    required: 'no',
    rule: required
      ? 'Obrigatória sem o ID interno (com a matrícula). Código de uma empresa onde você administra colaboradores'
      : 'Código de uma empresa onde você administra colaboradores',
    reference: { entity: 'Empresas', list: 'legal_entities' },
    example: 'FIC',
    width: 14,
  },
  {
    key: 'registration',
    header: 'Matrícula',
    description: 'Matrícula do vínculo na empresa.',
    type: 'text',
    max: 30,
    required: 'no',
    rule: required
      ? 'Obrigatória sem o ID interno (com a empresa). Letras, números, ponto, barra ou hífen (até 30)'
      : 'Letras, números, ponto, barra ou hífen (até 30)',
    example: '9001',
    width: 12,
  },
];

const RESTRICTED_RULE =
  'Restrito: exige as permissões de dados pessoais e de importar dados restritos, e fica bloqueado nesta instalação até a validação de segurança.';

/** Nível do centro de custo (ADR 0047). */
export const COST_CENTER_LEVEL_VALUES = [
  { label: 'Master', value: 'master' },
  { label: 'Gerencial', value: 'managerial' },
  { label: 'Operacional', value: 'operational' },
] as const;

const STATUS_VALUES = [
  { label: 'Ativo', value: 'active' },
  { label: 'Inativo', value: 'inactive' },
] as const;

const status = (example = 'Ativo'): ImportColumn => ({
  key: 'status',
  header: 'Situação',
  description: 'Ativo ou Inativo. Vazio ao incluir: Ativo. Vazio ao atualizar: mantém.',
  type: 'enum',
  required: 'no',
  rule: 'Ativo ou Inativo',
  values: STATUS_VALUES,
  reference: { entity: 'Situação', list: 'status' },
  example,
  width: 12,
});

const catalogCode = (header: string, description: string, example: string): ImportColumn => ({
  key: 'code',
  header,
  description,
  type: 'code',
  max: 20,
  required: 'always',
  rule: 'Letras, números, ponto, hífen ou _ (até 20). Formatado como texto: zeros à esquerda são mantidos. Não muda depois de gravado.',
  key_part: true,
  example,
  width: 14,
});

const description = (max = 2000): ImportColumn => ({
  key: 'description',
  header: 'Descrição',
  description: 'Texto livre. Vazio ao atualizar: mantém.',
  type: 'text',
  max,
  required: 'no',
  rule: `Até ${max} caracteres`,
  example: 'Descrição fictícia',
  width: 40,
});

const CATALOG_PERMISSIONS = ['career.catalog.manage'] as const;
const CATALOG_READ = [
  'people.org.read',
  'career.catalog.manage',
  'career.salary.read',
  'career.salary.manage',
] as const;

const COMMON_UPDATE_RULES = [
  'A chave identifica o registro: se já existir, a linha atualiza; se não existir, inclui.',
  'Célula vazia em campo opcional mantém o valor atual (para não apagar nada sem querer).',
  'Nada é excluído pela importação: o que não está na planilha não muda. Para desativar, use Situação = Inativo.',
  'Linha igual ao cadastro atual aparece como "sem alteração" e não grava nada.',
];

/** Valores dos steps salariais (−2 a +4, como na planilha oficial da LOOT). */
export const SALARY_STEPS = [-2, -1, 0, 1, 2, 3, 4] as const;
const stepHeader = (n: number) => `Step ${n > 0 ? `+${n}` : n}`;

// ----- Fornecedores e parceiros (cadastro único, ADR 0014; auditoria de 27/10/2026, item J) -----

const PARTNER_WRITE = ['partner.partner.manage'] as const;
const PARTNER_READ = [
  'partner.partner.read',
  'partner.partner.manage',
  'partner.partner.approve',
] as const;

const PERSON_TYPE_VALUES = [
  { label: 'PJ', value: 'company' },
  { label: 'PF', value: 'individual' },
] as const;

const TAX_ID_TYPE_VALUES = [
  { label: 'CNPJ', value: 'cnpj' },
  { label: 'CPF', value: 'cpf' },
  { label: 'Estrangeiro', value: 'foreign' },
  { label: 'Sem documento', value: 'none' },
] as const;

const partnerText = (key: string, header: string, max: number, description: string) =>
  ({
    key,
    header,
    description,
    type: 'text',
    max,
    required: 'no',
    rule: `Até ${max} caracteres`,
    width: 18,
  }) as const satisfies ImportColumn;

const PARTNER_ADDRESS_COLUMNS: readonly ImportColumn[] = [
  partnerText('addressStreet', 'Logradouro', 200, 'Rua, avenida etc.'),
  partnerText('addressNumber', 'Número', 20, 'Número do endereço.'),
  partnerText('addressComplement', 'Complemento', 100, 'Sala, andar etc.'),
  partnerText('addressDistrict', 'Bairro', 100, 'Bairro.'),
  partnerText('addressCity', 'Cidade', 100, 'Cidade.'),
  {
    ...partnerText(
      'addressRegion',
      'Estado',
      100,
      'No Brasil, a UF (ex.: SP); fora, o estado ou a região.',
    ),
    rule: 'No Brasil, uma UF válida',
  },
  {
    ...partnerText(
      'addressPostalCode',
      'CEP',
      20,
      'No Brasil, o CEP com 8 números; fora, o código postal.',
    ),
    rule: 'No Brasil, 8 números',
  },
];

/** Como os modelos de vínculos e contatos acham o fornecedor (a chave natural). */
const PARTNER_KEY_COLUMNS: readonly ImportColumn[] = [
  {
    key: 'country',
    header: 'País',
    description: 'País do fornecedor, em 2 letras (ISO 3166).',
    type: 'code',
    max: 2,
    required: 'always',
    rule: '2 letras (ex.: BR, US)',
    key_part: true,
    example: 'BR',
    width: 8,
  },
  {
    key: 'taxIdType',
    header: 'Tipo de documento',
    description: 'CNPJ, CPF, Estrangeiro ou Sem documento, como no cadastro do fornecedor.',
    type: 'enum',
    required: 'always',
    rule: 'CNPJ, CPF, Estrangeiro ou Sem documento',
    key_part: true,
    values: TAX_ID_TYPE_VALUES,
    example: 'CNPJ',
    width: 16,
  },
  {
    key: 'taxId',
    header: 'Documento',
    description: 'Documento do fornecedor (a pontuação é retirada). Vazio só em Sem documento.',
    type: 'text',
    max: 40,
    required: 'no',
    rule: 'Obrigatório, menos em Sem documento',
    example: '11.222.333/0001-81',
    width: 20,
  },
  {
    key: 'altIdentifier',
    header: 'Identificador alternativo',
    description: 'Só em Sem documento: o mesmo identificador alternativo do cadastro.',
    type: 'text',
    max: 200,
    required: 'no',
    rule: 'Em Sem documento: obrigatório',
    width: 30,
  },
];

export const IMPORT_KINDS: readonly ImportKind[] = [
  {
    id: 'job_families',
    group: 'career',
    order: 1,
    version: 1,
    title: 'Famílias Profissionais',
    identity: 'Código da família',
    dependsOn: [],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: COMMON_UPDATE_RULES,
    columns: [
      catalogCode('Código', 'Código oficial da família (ex.: 1 a 10 na planilha da LOOT).', '9'),
      {
        key: 'name',
        header: 'Nome',
        description: 'Nome da família. Único.',
        type: 'text',
        max: 120,
        required: 'create',
        rule: 'Até 120 caracteres; não pode repetir o de outra família',
        example: 'Família Fictícia',
        width: 30,
      },
      {
        key: 'shortCode',
        header: 'Prefixo',
        description: 'Prefixo usado nos nomes reduzidos dos cargos (ex.: BKO). Único.',
        type: 'code',
        max: 10,
        required: 'no',
        rule: '2 a 10 letras ou números',
        example: 'FIC',
        width: 10,
      },
      description(),
      status(),
    ],
  },
  {
    id: 'job_subfamilies',
    group: 'career',
    order: 2,
    version: 1,
    title: 'Subfamílias Profissionais',
    identity: 'Código da subfamília',
    dependsOn: ['job_families'],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'A família de uma subfamília já usada como principal de cargos de outra família não muda.',
    ],
    columns: [
      catalogCode('Código', 'Código oficial da subfamília.', '99'),
      {
        key: 'family',
        header: 'Família (código)',
        description: 'Código da família a que pertence. A família precisa existir.',
        type: 'reference',
        max: 20,
        required: 'create',
        rule: 'Código de uma família cadastrada',
        reference: { entity: 'Famílias Profissionais', list: 'families' },
        example: '9',
        width: 14,
      },
      {
        key: 'name',
        header: 'Nome',
        description: 'Nome da subfamília. Único dentro da família.',
        type: 'text',
        max: 120,
        required: 'create',
        rule: 'Até 120 caracteres; não repete na mesma família',
        example: 'Subfamília Fictícia',
        width: 30,
      },
      {
        key: 'kind',
        header: 'Tipo',
        description: 'Estrutural, Trilha estrutural de cargo ou Subfamília de atuação.',
        type: 'enum',
        required: 'no',
        rule: 'Um dos três tipos da lista',
        values: [
          { label: 'Estrutural', value: 'structural' },
          { label: 'Trilha estrutural de cargo', value: 'structural_track' },
          { label: 'Subfamília de atuação', value: 'practice' },
        ],
        reference: { entity: 'Tipos de subfamília', list: 'subfamily_kinds' },
        example: 'Estrutural',
        width: 26,
      },
      description(),
      status(),
    ],
  },
  {
    id: 'job_levels',
    group: 'career',
    order: 3,
    version: 1,
    title: 'Níveis JL',
    identity: 'Código do nível (ex.: JL05)',
    dependsOn: [],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'A denominação aqui é a genérica do nível. O título de carreira de cada JL varia por trilha e família e fica no próprio cargo (denominação e nome mercadológico).',
    ],
    columns: [
      catalogCode('Código', 'Código do nível (ex.: JL01 a JL12, ETG, PJU).', 'JL99'),
      {
        key: 'name',
        header: 'Denominação',
        description: 'Denominação genérica do nível.',
        type: 'text',
        max: 120,
        required: 'create',
        rule: 'Até 120 caracteres',
        example: 'Nível Fictício',
        width: 26,
      },
      {
        key: 'rank',
        header: 'Ordem',
        description: 'Ordem hierárquica (número inteiro; maior = mais alto).',
        type: 'integer',
        required: 'create',
        rule: 'Número inteiro de 0 a 999',
        example: '99',
        width: 8,
      },
      description(),
      status(),
    ],
  },
  {
    id: 'career_tracks',
    group: 'career',
    order: 4,
    version: 1,
    title: 'Trilhas Profissionais',
    identity: 'Código da trilha',
    dependsOn: [],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'A trilha é corporativa (Base, Técnica, Liderança...): não tem família no cadastro. A família de cada cargo fica no Catálogo de Cargos.',
    ],
    columns: [
      catalogCode('Código', 'Código da trilha (ex.: BASE, TECNICA, LIDERANCA).', 'FICTICIA'),
      {
        key: 'name',
        header: 'Nome',
        description: 'Nome da trilha. Único.',
        type: 'text',
        max: 120,
        required: 'create',
        rule: 'Até 120 caracteres; não pode repetir o de outra trilha',
        example: 'Trilha Fictícia',
        width: 26,
      },
      description(),
      status(),
    ],
  },
  {
    id: 'salary_grades',
    group: 'career',
    order: 5,
    version: 1,
    title: 'Grades Salariais',
    identity: 'Código da grade',
    dependsOn: ['job_families', 'job_levels'],
    prerequisites: ['Moedas'],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'Os valores dos steps são opcionais e restritos: só entram com as permissões de valores salariais (alterar valores salariais e importar valores salariais), e nunca saem no template de atualização.',
      'Step vazio não grava nada (nenhum valor é inventado). Um valor gravado não muda: a mesma vigência com outro valor é recusada; para mudar, informe outra vigência (a anterior em aberto termina na véspera).',
      'Nenhum valor é atribuído a colaboradores e não há cálculo de folha.',
    ],
    columns: [
      {
        key: 'code',
        header: 'Código',
        description: 'Identificador oficial da grade (ex.: família e JL).',
        type: 'code',
        max: 30,
        required: 'always',
        rule: 'Letras, números, ponto, hífen ou _ (até 30). Não muda depois de gravado.',
        key_part: true,
        example: 'FIC-JL99',
        width: 16,
      },
      {
        key: 'family',
        header: 'Família (código)',
        description: 'Família da grade.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma família cadastrada',
        reference: { entity: 'Famílias Profissionais', list: 'families' },
        example: '9',
        width: 14,
      },
      {
        key: 'level',
        header: 'Nível JL (código)',
        description: 'Nível JL da grade.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de um nível cadastrado',
        reference: { entity: 'Níveis JL', list: 'levels' },
        example: 'JL99',
        width: 14,
      },
      description(),
      status(),
      {
        key: 'validFrom',
        header: 'Vigência dos valores',
        description:
          'Início de vigência dos valores dos steps desta linha. Obrigatória se algum step for preenchido.',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        permission: 'career.salary.manage',
        restricted: true,
        example: '01/01/2026',
        width: 14,
      },
      {
        key: 'currency',
        header: 'Moeda',
        description: 'Moeda dos valores (3 letras). Obrigatória se algum step for preenchido.',
        type: 'reference',
        max: 3,
        required: 'no',
        rule: 'Moeda cadastrada (ex.: BRL)',
        reference: { entity: 'Moedas', list: 'currencies' },
        permission: 'career.salary.manage',
        restricted: true,
        example: 'BRL',
        width: 8,
      },
      ...SALARY_STEPS.map((n): ImportColumn => ({
        key: `step${n}`,
        header: stepHeader(n),
        description: `Valor de referência do step ${n} (opcional, restrito).`,
        type: 'decimal',
        required: 'no',
        rule: 'Valor positivo com até 4 casas decimais',
        permission: 'career.salary.manage',
        restricted: true,
        example: '',
        width: 11,
      })),
    ],
  },
  {
    id: 'jobs',
    group: 'career',
    order: 6,
    version: 1,
    title: 'Catálogo de Cargos',
    identity: 'Código oficial do cargo',
    dependsOn: ['job_families', 'job_subfamilies', 'job_levels', 'career_tracks', 'salary_grades'],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'O código oficial não muda. Código da função e nome reduzido também são únicos.',
      'As subfamílias de atuação (além da principal) entram pelo template "Relacionamento Cargos × Subfamílias"; esta importação não as altera.',
    ],
    columns: [
      catalogCode('Código oficial', 'Código oficial do cargo (6 dígitos na LOOT).', '999001'),
      {
        key: 'functionCode',
        header: 'Código da função',
        description: 'Código da função (ADP). Único.',
        type: 'code',
        max: 20,
        required: 'no',
        rule: 'Letras, números, ponto, hífen ou _ (até 20); único',
        example: '999101',
        width: 14,
      },
      {
        key: 'shortName',
        header: 'Nome reduzido',
        description: 'Identificador da folha (ex.: BKO-JL01-001). Único.',
        type: 'text',
        max: 30,
        required: 'no',
        rule: 'Até 30 caracteres; único',
        example: 'FIC-JL99-001',
        width: 16,
      },
      {
        key: 'name',
        header: 'Denominação oficial',
        description: 'Nome oficial do cargo.',
        type: 'text',
        max: 200,
        required: 'create',
        rule: '2 a 200 caracteres',
        example: 'ANALISTA FICTÍCIO',
        width: 32,
      },
      {
        key: 'marketName',
        header: 'Nome mercadológico',
        description: 'Título de mercado.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: '2 a 200 caracteres',
        example: 'Analista Fictício',
        width: 28,
      },
      { ...description(4000), header: 'Descrição da função' },
      {
        key: 'family',
        header: 'Família (código)',
        description: 'Família do cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma família cadastrada',
        reference: { entity: 'Famílias Profissionais', list: 'families' },
        example: '9',
        width: 14,
      },
      {
        key: 'subfamily',
        header: 'Subfamília principal (código)',
        description: 'Subfamília principal; precisa ser da família do cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma subfamília da mesma família',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '99',
        width: 16,
      },
      {
        key: 'level',
        header: 'Nível JL (código)',
        description: 'Nível JL do cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de um nível cadastrado',
        reference: { entity: 'Níveis JL', list: 'levels' },
        example: 'JL99',
        width: 14,
      },
      {
        key: 'track',
        header: 'Trilha (código)',
        description: 'Trilha profissional do cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma trilha cadastrada',
        reference: { entity: 'Trilhas Profissionais', list: 'tracks' },
        example: 'FICTICIA',
        width: 14,
      },
      {
        key: 'cbo',
        header: 'CBO',
        description: 'Classificação Brasileira de Ocupações (6 dígitos).',
        type: 'code',
        max: 6,
        required: 'no',
        rule: '6 números',
        example: '999999',
        width: 9,
      },
      {
        key: 'grade',
        header: 'Grade (código)',
        description: 'Grade salarial do cargo (só o código; nenhum valor).',
        type: 'reference',
        max: 30,
        required: 'no',
        rule: 'Código de uma grade cadastrada',
        reference: { entity: 'Grades Salariais', list: 'grades' },
        example: 'FIC-JL99',
        width: 14,
      },
      {
        key: 'validFrom',
        header: 'Início de vigência',
        description: 'Início de vigência do cargo.',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '01/01/2026',
        width: 13,
      },
      {
        key: 'validUntil',
        header: 'Fim de vigência',
        description: 'Fim de vigência do cargo.',
        type: 'date',
        required: 'no',
        rule: 'Data; não antes do início',
        example: '',
        width: 13,
      },
      status(),
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observações administrativas (ex.: status da reestruturação).',
        type: 'text',
        max: 2000,
        required: 'no',
        rule: 'Até 2000 caracteres',
        example: '',
        width: 30,
      },
    ],
  },
  {
    id: 'job_practices',
    group: 'career',
    order: 7,
    version: 1,
    title: 'Relacionamento Cargos × Subfamílias',
    identity: 'Código do cargo + código da subfamília',
    dependsOn: ['jobs', 'job_subfamilies'],
    prerequisites: [],
    applyPermissions: CATALOG_PERMISSIONS,
    readPermissions: CATALOG_READ,
    transaction: 'atomic',
    updateRules: [
      'Cada linha liga um cargo a uma subfamília de atuação (além da principal). Um cargo pode ter várias.',
      'O par cargo + subfamília identifica a ligação: se já existir, a linha atualiza o nome mercadológico.',
      'Nada é desligado pela importação: ligações fora da planilha continuam.',
      'A subfamília principal do cargo não entra aqui (ela já é do cargo).',
    ],
    columns: [
      {
        key: 'job',
        header: 'Cargo (código oficial)',
        description: 'Código oficial do cargo.',
        type: 'reference',
        max: 20,
        required: 'always',
        rule: 'Código de um cargo cadastrado',
        key_part: true,
        reference: { entity: 'Catálogo de Cargos', list: 'jobs' },
        example: '999001',
        width: 16,
      },
      {
        key: 'subfamily',
        header: 'Subfamília (código)',
        description: 'Código da subfamília de atuação.',
        type: 'reference',
        max: 20,
        required: 'always',
        rule: 'Código de uma subfamília cadastrada (que não seja a principal do cargo)',
        key_part: true,
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '98',
        width: 16,
      },
      {
        key: 'marketName',
        header: 'Nome mercadológico na subfamília',
        description: 'Título de mercado do cargo nesta frente de atuação.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: '2 a 200 caracteres',
        example: 'Analista Fictício de Atuação',
        width: 32,
      },
    ],
  },
  {
    id: 'cost_centers',
    group: 'structure',
    order: 8,
    version: 5,
    title: 'Centros de Custo',
    identity: 'Empresa + código do centro de custo',
    dependsOn: [],
    prerequisites: ['Empresas', 'Departamentos', 'Usuários (gestores e substitutos)'],
    applyPermissions: ['core.cost_center.manage'],
    readPermissions: ['core.cost_center.read', 'core.cost_center.manage', 'structure.catalog.read'],
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'Grava em core.cost_center (o mesmo cadastro da tela de Centros de Custo). Empresas não são criadas: o código da empresa precisa existir.',
      'Hierarquia (adendo 2 do ADR 0047): o Gerencial informa o Centro de Custo Master; o Operacional informa o Master e, se houver, o Centro de Custo Gerencial, que precisa estar abaixo desse Master. Sem Gerencial, o Operacional fica direto no Master. O Master não informa nenhum dos dois. O sistema grava só o superior direto (o Gerencial ou, sem ele, o Master).',
      'Centro superior efetivo: vem preenchido no template de atualização, só para conferência. Se for informado junto com Master e Gerencial, precisa bater com eles. Em centro sem nível (em regularização), ele continua sendo o jeito de informar o superior, como antes.',
      'Os centros de cima podem estar no mesmo arquivo: a importação grava primeiro os superiores (um envio só). Ciclo na hierarquia é recusado. Célula vazia mantém o superior atual.',
      'Início de vigência: sem data na linha, vale a data informada para o lote na tela; sem nenhuma das duas, a linha fica pendente (a data atual nunca é presumida).',
      'Um centro de custo não muda de empresa. A inativação segue as regras do cadastro (não inativa com centros abaixo ativos, colaboradores ou rateios vigentes).',
      'A coluna "Unidade de Negócio" da planilha de origem da LOOT corresponde à empresa (código), não à unidade de negócio gerencial do FP&A.',
      'Nível (ADR 0047): Master não tem superior; Gerencial fica abaixo de um Master; Operacional, abaixo de um Gerencial ou de um Master. O superior precisa ter nível. Centro novo ativo: Nível e gestor do centro de custo obrigatórios. Centro que já existe: vazio mantém o que está gravado (regularização aos poucos).',
      'Gestor do centro de custo e substituto: pelo e-mail do usuário. São eles que aprovam a necessidade das compras, a dispensa de termo do Patrimônio e as etapas de gestor da matriz. O Master Responsável é calculado pela estrutura e não entra neste modelo (ADR 0099).',
    ],
    options: [
      {
        key: 'defaultValidFrom',
        label: 'Início de vigência para as linhas sem data',
        type: 'date',
        help: 'Opcional. Só vale para linhas de centros novos sem "Início de vigência". Sem ela, essas linhas ficam pendentes.',
      },
    ],
    columns: [
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description:
          'Código da empresa no cadastro de Empresas (na planilha da LOOT: a "Unidade de Negócio").',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa cadastrada, onde você administra centros de custo',
        key_part: true,
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'code',
        header: 'Código',
        description: 'Código oficial do centro de custo (9 dígitos na LOOT). Formatado como texto.',
        type: 'code',
        max: 40,
        required: 'always',
        rule: 'Letras, números, ponto, hífen ou _ (até 40); zeros à esquerda são mantidos. Não muda depois de gravado.',
        key_part: true,
        example: '990101001',
        width: 14,
      },
      {
        key: 'name',
        header: 'Descrição',
        description: 'Nome do centro de custo.',
        type: 'text',
        max: 200,
        required: 'create',
        rule: 'Até 200 caracteres',
        example: 'Centro Fictício',
        width: 32,
      },
      {
        key: 'department',
        header: 'Departamento (código)',
        description: 'Departamento da empresa (ou corporativo).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de um departamento da empresa ou corporativo',
        reference: { entity: 'Departamentos', list: 'departments' },
        example: '',
        width: 14,
      },
      {
        key: 'master',
        header: 'Centro de Custo Master (código)',
        description:
          'Para Gerencial e Operacional: o centro Master acima, ativo e da mesma empresa. Pode estar no mesmo arquivo.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Código de um centro Master ativo da mesma empresa (cadastrado ou no arquivo)',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '990100000',
        width: 18,
      },
      {
        key: 'managerial',
        header: 'Centro de Custo Gerencial (código)',
        description:
          'Só para Operacional, opcional: o centro Gerencial abaixo do Master informado. Vazio = direto no Master.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Código de um centro Gerencial ativo, abaixo do Master informado',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '990101000',
        width: 18,
      },
      {
        key: 'parent',
        header: 'Centro superior efetivo (código)',
        description:
          'O superior direto gravado (conferência). Em centro sem nível, informa o superior, como antes.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Código de um centro da mesma empresa; com Master e Gerencial, precisa bater com eles',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '990101000',
        width: 18,
      },
      {
        key: 'manager',
        header: 'Gestor do centro de custo (e-mail do usuário)',
        description:
          'E-mail do usuário que aprova a necessidade das compras e a dispensa de termo do Patrimônio deste centro (cadastrado e ativo em Usuários). Obrigatório, com o Nível, em centro novo ativo.',
        type: 'email',
        max: 254,
        required: 'no',
        rule: 'E-mail de um usuário ativo',
        example: 'gestor.ficticio@exemplo.test',
        width: 28,
      },
      {
        key: 'substitute',
        header: 'Substituto do gestor (e-mail do usuário)',
        description: 'E-mail do usuário substituto do gestor (outra pessoa, ativa).',
        type: 'email',
        max: 254,
        required: 'no',
        rule: 'E-mail de um usuário ativo, diferente do gestor',
        example: '',
        width: 28,
      },
      status(),
      {
        key: 'validFrom',
        header: 'Início de vigência',
        description: 'Início de vigência. Sem data: vale a do lote (informada na tela).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '01/01/2026',
        width: 13,
      },
      {
        key: 'validUntil',
        header: 'Término de vigência',
        description: 'Término de vigência (opcional).',
        type: 'date',
        required: 'no',
        rule: 'Data; não antes do início',
        example: '',
        width: 13,
      },
      {
        key: 'netsuiteCode',
        header: 'Código NetSuite',
        description: 'Referência externa (só o código; sem integração).',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Até 100 caracteres',
        example: '',
        width: 14,
      },
      {
        key: 'structureLevel',
        header: 'Nível',
        description: 'Master, Gerencial ou Operacional. Vazio ao atualizar: mantém.',
        type: 'enum',
        required: 'no',
        rule: 'Master, Gerencial ou Operacional',
        values: COST_CENTER_LEVEL_VALUES,
        reference: { entity: 'Níveis', list: 'cost_center_levels' },
        example: 'Operacional',
        width: 14,
      },
    ],
  },
  {
    id: 'persons',
    group: 'people',
    order: 9,
    version: 1,
    title: 'Pessoas',
    identity:
      'ID interno; sem ele, e-mail corporativo ou CPF (com permissão). Sem nenhum: pessoa nova (com conferência de nomes parecidos)',
    dependsOn: [],
    prerequisites: [],
    applyPermissions: ['people.person.manage'],
    readPermissions: ['people.person.manage', 'people.private.read', 'people.private.manage'],
    transaction: 'atomic',
    updateRules: [
      'Identificação: ID interno (template de atualização); sem ele, e-mail corporativo; sem os dois, o CPF (só com as permissões de dados restritos).',
      'Sem identificador, a linha cria uma pessoa nova. Se já houver pessoa com nome parecido, a linha fica "para conciliar": informe o ID interno da pessoa certa ou escreva Sim em "Confirmar pessoa nova". Nomes nunca associam pessoas automaticamente.',
      'Só o nome completo é obrigatório. O CPF não é exigido.',
      'Célula vazia mantém o valor atual. Nada é excluído; para desativar, Situação = Inativo.',
      'Não cria usuário de acesso nem concede perfis.',
      'CPF: ' +
        RESTRICTED_RULE +
        ' O CPF nunca volta na prévia, no relatório de erros nem no template de atualização.',
    ],
    columns: [
      {
        key: 'id',
        header: 'ID interno',
        description:
          'Identificador da pessoa (vem no template de atualização). Vazio = pessoa identificada pelo e-mail, CPF ou nova.',
        type: 'code',
        max: 36,
        required: 'no',
        rule: 'Não altere: identifica a pessoa',
        example: '',
        width: 38,
      },
      {
        key: 'fullName',
        header: 'Nome completo',
        description: 'Nome civil completo.',
        type: 'text',
        max: 200,
        required: 'always',
        rule: '2 a 200 caracteres',
        key_part: true,
        example: 'Pessoa Fictícia da Silva',
        width: 32,
      },
      {
        key: 'socialName',
        header: 'Nome social',
        description: 'Nome social, quando houver.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 24,
      },
      {
        key: 'displayName',
        header: 'Nome para exibição',
        description: 'Como a pessoa aparece nas telas.',
        type: 'text',
        max: 120,
        required: 'no',
        rule: 'Até 120 caracteres',
        example: 'Pessoa Fictícia',
        width: 22,
      },
      {
        key: 'corporateEmail',
        header: 'E-mail corporativo',
        description: 'E-mail da empresa. Único: identifica a pessoa quando não há ID interno.',
        type: 'email',
        max: 254,
        required: 'no',
        rule: 'E-mail válido; não pode ser de outra pessoa',
        example: 'pessoa.ficticia@exemplo.test',
        width: 30,
      },
      status(),
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observações administrativas.',
        type: 'text',
        max: 2000,
        required: 'no',
        rule: 'Até 2000 caracteres',
        example: '',
        width: 30,
      },
      {
        key: 'cpf',
        header: 'CPF',
        description:
          'CPF (opcional). Guardado cifrado; identifica a pessoa quando não há ID nem e-mail.',
        type: 'code',
        max: 14,
        required: 'no',
        rule:
          '11 números (com ou sem pontuação), dígitos verificadores válidos. ' + RESTRICTED_RULE,
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 16,
      },
      {
        key: 'confirmNew',
        header: 'Confirmar pessoa nova',
        description:
          'Sim = cria a pessoa mesmo havendo nome parecido já cadastrado (depois de conferir).',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou vazio',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: '',
        width: 12,
      },
    ],
  },
  {
    id: 'employees',
    group: 'people',
    order: 10,
    version: 1,
    title: 'Colaboradores',
    identity: 'ID interno; sem ele, empresa + matrícula; sem matrícula, a pessoa + empresa',
    dependsOn: ['persons', 'jobs', 'cost_centers'],
    prerequisites: ['Empresas', 'Filiais', 'Departamentos', 'Tipos de vínculo', 'Locais'],
    applyPermissions: ['people.employee.manage'],
    readPermissions: PEOPLE_READ,
    transaction: 'atomic',
    updateRules: [
      'Identificação: ID interno; sem ele, empresa + matrícula; sem matrícula, a pessoa (ID interno ou e-mail corporativo) + empresa. Nome nunca identifica.',
      'A pessoa precisa existir (importe Pessoas antes). Não cria usuário de acesso nem concede perfis.',
      'Célula vazia mantém o valor atual. A data de admissão nunca é presumida.',
      'Situação inicial (cargo, área, gestor, centro de custo...): só é gravada quando "Situação inicial: vigência" está preenchida e o colaborador ainda não tem histórico. Com histórico, mudanças entram pelo template de Movimentações (aqui ficam como aviso, sem gravar).',
      'Gestor: precisa ter situação funcional ativa na data. Informe a matrícula (e a empresa, se for de outra).',
      'Subfamília de atuação: a principal do cargo ou uma das de atuação dele, ativa. Registra onde a pessoa de fato atua; o cargo e o catálogo não mudam.',
      'Data de término: os rateios em aberto terminam na mesma data (o histórico não muda).',
    ],
    columns: [
      {
        key: 'id',
        header: 'ID interno',
        description: 'Identificador do colaborador (vem no template de atualização).',
        type: 'code',
        max: 36,
        required: 'no',
        rule: 'Não altere: identifica o vínculo',
        example: '',
        width: 38,
      },
      {
        key: 'personId',
        header: 'Pessoa: ID interno',
        description:
          'ID interno da pessoa (template de atualização de Pessoas). Obrigatório ao incluir, ou o e-mail corporativo.',
        type: 'code',
        max: 36,
        required: 'no',
        rule: 'ID de uma pessoa cadastrada',
        example: '',
        width: 38,
      },
      {
        key: 'personEmail',
        header: 'Pessoa: e-mail corporativo',
        description: 'E-mail corporativo da pessoa (alternativa ao ID interno).',
        type: 'email',
        max: 254,
        required: 'no',
        rule: 'E-mail de uma pessoa cadastrada',
        example: 'pessoa.ficticia@exemplo.test',
        width: 30,
      },
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description: 'Empresa empregadora ou contratante.',
        type: 'reference',
        max: 40,
        required: 'create',
        rule: 'Código de uma empresa onde você administra colaboradores',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'registration',
        header: 'Matrícula',
        description:
          'Matrícula na empresa (única por empresa). Opcional: o cadastro pode ser progressivo.',
        type: 'text',
        max: 30,
        required: 'no',
        rule: 'Letras, números, ponto, barra ou hífen (até 30)',
        example: '9001',
        width: 12,
      },
      {
        key: 'externalCode',
        header: 'Código externo',
        description: 'Código em outro sistema (ex.: folha). Único.',
        type: 'text',
        max: 60,
        required: 'no',
        rule: 'Até 60 caracteres',
        example: '',
        width: 14,
      },
      {
        key: 'employmentType',
        header: 'Tipo de vínculo (código)',
        description: 'CLT, PJ, estágio...',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de tipos de vínculo cadastrado(a)',
        reference: { entity: 'Tipos de vínculo', list: 'employment_types' },
        example: 'CLT',
        width: 14,
      },
      {
        key: 'startDate',
        header: 'Data de admissão',
        description: 'Data de admissão ou início (nunca presumida).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '05/01/2026',
        width: 13,
      },
      {
        key: 'endDate',
        header: 'Data de término',
        description: 'Término do vínculo (encerra os rateios em aberto na mesma data).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '',
        width: 13,
      },
      {
        key: 'workday',
        header: 'Jornada',
        description: 'Jornada contratual (ex.: 44h semanais, segunda a sexta).',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 24,
      },
      {
        key: 'weeklyHours',
        header: 'Carga horária semanal',
        description: 'Horas por semana.',
        type: 'decimal',
        required: 'no',
        rule: 'Número maior que 0 e até 168',
        example: '40',
        width: 10,
      },
      {
        key: 'unionName',
        header: 'Sindicato',
        description: 'Sindicato da categoria.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 20,
      },
      {
        key: 'collectiveAgreement',
        header: 'Convenção coletiva',
        description: 'Convenção ou acordo coletivo.',
        type: 'text',
        max: 300,
        required: 'no',
        rule: 'Até 300 caracteres',
        example: '',
        width: 20,
      },
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observações administrativas.',
        type: 'text',
        max: 2000,
        required: 'no',
        rule: 'Até 2000 caracteres',
        example: '',
        width: 24,
      },
      {
        key: 'initialFrom',
        header: 'Situação inicial: vigência',
        description:
          'Data a partir da qual vale a situação inicial abaixo. Vazia = nenhuma situação é gravada.',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '',
        width: 13,
      },
      {
        key: 'initialStatus',
        header: 'Situação funcional',
        description: 'Situação inicial do vínculo (com a vigência). Vazio = Ativo.',
        type: 'enum',
        required: 'no',
        rule: 'Ativo, Desligado ou Encerrado',
        values: EMPLOYMENT_STATUS_VALUES,
        reference: { entity: 'Situações funcionais', list: 'employment_statuses' },
        example: 'Ativo',
        width: 12,
      },
      {
        key: 'branch',
        header: 'Filial (código)',
        description: 'Filial da empresa.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de filiais cadastrado(a)',
        reference: { entity: 'Filiais', list: 'branches' },
        example: '',
        width: 12,
      },
      {
        key: 'job',
        header: 'Cargo (código oficial)',
        description: 'Cargo do catálogo.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de cargos cadastrado(a)',
        reference: { entity: 'Cargos', list: 'jobs' },
        example: '999001',
        width: 14,
      },
      {
        key: 'subfamily',
        header: 'Subfamília de atuação (código)',
        description:
          'Subfamília em que a pessoa de fato atua: a principal do cargo ou uma das de atuação dele. Não altera o cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma subfamília ativa do cargo (principal ou de atuação); exige o cargo',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '',
        width: 16,
      },
      {
        key: 'department',
        header: 'Departamento (código)',
        description: 'Departamento da empresa ou corporativo.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de departamentos cadastrado(a)',
        reference: { entity: 'Departamentos', list: 'departments' },
        example: '',
        width: 14,
      },
      {
        key: 'managerEntity',
        header: 'Gestor: empresa (código)',
        description: 'Empresa do gestor, se for outra.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de empresas cadastrado(a)',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: '',
        width: 14,
      },
      {
        key: 'managerRegistration',
        header: 'Gestor: matrícula',
        description: 'Matrícula do gestor imediato (com situação funcional ativa).',
        type: 'text',
        max: 30,
        required: 'no',
        rule: 'Matrícula de um colaborador ativo',
        example: '',
        width: 12,
      },
      {
        key: 'topLevel',
        header: 'Topo da estrutura',
        description: 'Sim = não tem gestor imediato (não conta como pendência).',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: '',
        width: 10,
      },
      {
        key: 'costCenter',
        header: 'Centro de custo principal (código)',
        description: 'Centro de custo principal (ativo, da empresa).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de centros de custo cadastrado(a)',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '',
        width: 16,
      },
      {
        key: 'location',
        header: 'Local (código)',
        description: 'Local de trabalho.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de locais cadastrado(a)',
        reference: { entity: 'Locais', list: 'locations' },
        example: '',
        width: 12,
      },
      {
        key: 'workMode',
        header: 'Modalidade',
        description: 'Presencial, Híbrido ou Remoto.',
        type: 'enum',
        required: 'no',
        rule: 'Uma das modalidades',
        values: WORK_MODE_VALUES,
        reference: { entity: 'Modalidades', list: 'work_modes' },
        example: 'Híbrido',
        width: 12,
      },
    ],
  },
  {
    id: 'movements',
    group: 'people',
    order: 11,
    version: 2,
    title: 'Movimentações Funcionais',
    identity: 'Colaborador (ID interno ou empresa + matrícula) + data de vigência',
    dependsOn: ['employees'],
    prerequisites: [],
    applyPermissions: ['people.employee.manage'],
    readPermissions: PEOPLE_READ,
    transaction: 'atomic',
    updateRules: [
      'Cada linha é uma movimentação: a situação a partir da data de vigência. Só muda o que vier preenchido em "novo"; o resto continua como estava na data.',
      'Os campos "anterior" são conferência: se preenchidos, precisam bater com a situação na data (senão, a linha é recusada). Nenhuma movimentação é deduzida por diferença entre planilhas.',
      'Ordem: as linhas de cada colaborador são gravadas por data. Não entram datas anteriores à última movimentação já registrada (o histórico não é reescrito).',
      'A mesma movimentação de novo (mesma data, mesmos valores) aparece como "sem alteração". Na mesma data com outros valores, é recusada.',
      'Desligado ou Encerrado: grava o término do vínculo e encerra os rateios em aberto na data.',
      'Gestor: precisa ter situação funcional ativa na data.',
      'Subfamília de atuação: ativa; nos cargos da trilha Liderança, qualquer subfamília; nos demais, a principal do cargo ou uma das de atuação dele. O cargo e o catálogo não mudam. Trocando o cargo sem informar a subfamília, a anterior continua se valer para o novo cargo; se não valer, fica sem valor (com aviso).',
      'Também atua em, Atua em toda a família, Lidera famílias e Lidera subfamílias (ADR 0054): códigos separados por ";". Vazio mantém o que está registrado; "-" deixa a lista vazia. Toda a família e liderança só em cargo da trilha Liderança; outras atuações precisam da subfamília principal. Nada disso dá alçada de aprovação.',
    ],
    columns: [
      ...employeeRef(true),
      {
        key: 'validFrom',
        header: 'Vigência',
        description: 'Data a partir da qual vale a nova situação.',
        type: 'date',
        required: 'always',
        rule: 'Data (dd/mm/aaaa)',
        example: '01/03/2026',
        width: 13,
      },
      {
        key: 'kind',
        header: 'Tipo de movimentação',
        description: 'Classificação da movimentação (vai para o motivo).',
        type: 'enum',
        required: 'no',
        rule: 'Um dos tipos da lista',
        values: MOVEMENT_KIND_VALUES,
        reference: { entity: 'Tipos de movimentação', list: 'movement_kinds' },
        example: 'Promoção',
        width: 20,
      },
      {
        key: 'jobBefore',
        header: 'Cargo anterior (código)',
        description: 'Conferência: cargo na data.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de cargos cadastrado(a)',
        reference: { entity: 'Cargos', list: 'jobs' },
        example: '',
        width: 14,
      },
      {
        key: 'job',
        header: 'Cargo novo (código)',
        description: 'Novo cargo.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de cargos cadastrado(a)',
        reference: { entity: 'Cargos', list: 'jobs' },
        example: '999002',
        width: 14,
      },
      {
        key: 'subfamilyBefore',
        header: 'Subfamília anterior (código)',
        description: 'Conferência: subfamília de atuação na data.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma subfamília cadastrada',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '',
        width: 14,
      },
      {
        key: 'subfamily',
        header: 'Subfamília de atuação nova (código)',
        description:
          'Subfamília em que a pessoa passa a atuar: a principal do cargo ou uma das de atuação dele. Não altera o cargo.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código de uma subfamília ativa (fora da trilha Liderança, do cargo: principal ou de atuação)',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '',
        width: 16,
      },
      {
        key: 'practiceSubfamilies',
        header: 'Também atua em (subfamílias, códigos separados por ;)',
        description:
          'Outras subfamílias em que a pessoa atua, além da principal. Vazio mantém; "-" deixa sem nenhuma.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de subfamílias ativas, separados por ";" (ou "-")',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '',
        width: 22,
      },
      {
        key: 'practiceFamilies',
        header: 'Atua em toda a família (códigos separados por ;)',
        description:
          'Famílias em que a pessoa atua inteiras (só cargo da trilha Liderança). Vale para as subfamílias ativas de cada uma, inclusive as criadas depois. Vazio mantém; "-" deixa sem nenhuma.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de famílias ativas, separados por ";" (ou "-")',
        reference: { entity: 'Famílias Profissionais', list: 'families' },
        example: '',
        width: 20,
      },
      {
        key: 'ledFamilies',
        header: 'Lidera famílias (códigos separados por ;)',
        description:
          'Famílias que a pessoa lidera inteiras (só cargo da trilha Liderança). Vazio mantém; "-" deixa sem nenhuma.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de famílias ativas, separados por ";" (ou "-")',
        reference: { entity: 'Famílias Profissionais', list: 'families' },
        example: '',
        width: 20,
      },
      {
        key: 'ledSubfamilies',
        header: 'Lidera subfamílias (códigos separados por ;)',
        description:
          'Subfamílias que a pessoa lidera (só cargo da trilha Liderança), fora das famílias lideradas inteiras. Vazio mantém; "-" deixa sem nenhuma.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de subfamílias ativas, separados por ";" (ou "-")',
        reference: { entity: 'Subfamílias Profissionais', list: 'subfamilies' },
        example: '',
        width: 22,
      },
      {
        key: 'practiceDepartments',
        header: 'Também atua nos departamentos (códigos separados por ;)',
        description:
          'Departamentos em que a pessoa também atua, além do principal. Não dá alçada. Vazio mantém; "-" deixa sem nenhum.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de departamentos ativos, separados por ";" (ou "-")',
        example: '',
        width: 22,
      },
      {
        key: 'ledDepartments',
        header: 'Lidera departamentos (códigos separados por ;)',
        description:
          'Departamentos que a pessoa lidera, além do principal (só cargo da trilha Liderança). Não dá alçada. Vazio mantém; "-" deixa sem nenhum.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Códigos de departamentos ativos, separados por ";" (ou "-")',
        example: '',
        width: 22,
      },
      {
        key: 'departmentBefore',
        header: 'Departamento anterior (código)',
        description: 'Conferência: departamento na data.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de departamentos cadastrado(a)',
        reference: { entity: 'Departamentos', list: 'departments' },
        example: '',
        width: 14,
      },
      {
        key: 'department',
        header: 'Departamento novo (código)',
        description: 'Novo departamento.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de departamentos cadastrado(a)',
        reference: { entity: 'Departamentos', list: 'departments' },
        example: '',
        width: 14,
      },
      {
        key: 'managerBefore',
        header: 'Gestor anterior (matrícula)',
        description: 'Conferência: matrícula do gestor na data.',
        type: 'text',
        max: 30,
        required: 'no',
        rule: 'Matrícula',
        example: '',
        width: 12,
      },
      {
        key: 'managerRegistration',
        header: 'Gestor novo (matrícula)',
        description: 'Matrícula do novo gestor (ativo na data).',
        type: 'text',
        max: 30,
        required: 'no',
        rule: 'Matrícula de um colaborador ativo',
        example: '',
        width: 12,
      },
      {
        key: 'managerEntity',
        header: 'Gestor: empresa (código)',
        description: 'Empresa do gestor, se for outra.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de empresas cadastrado(a)',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: '',
        width: 14,
      },
      {
        key: 'costCenterBefore',
        header: 'Centro de custo anterior (código)',
        description: 'Conferência: centro principal na data.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de centros de custo cadastrado(a)',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '',
        width: 16,
      },
      {
        key: 'costCenter',
        header: 'Centro de custo novo (código)',
        description: 'Novo centro principal (ativo, da empresa).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de centros de custo cadastrado(a)',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '',
        width: 16,
      },
      {
        key: 'newLegalEntity',
        header: 'Empresa nova (código)',
        description: 'Transferência: nova empresa (permissão nas duas).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de empresas cadastrado(a)',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: '',
        width: 14,
      },
      {
        key: 'branch',
        header: 'Filial nova (código)',
        description: 'Nova filial.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de filiais cadastrado(a)',
        reference: { entity: 'Filiais', list: 'branches' },
        example: '',
        width: 12,
      },
      {
        key: 'location',
        header: 'Local novo (código)',
        description: 'Novo local.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de locais cadastrado(a)',
        reference: { entity: 'Locais', list: 'locations' },
        example: '',
        width: 12,
      },
      {
        key: 'workMode',
        header: 'Modalidade nova',
        description: 'Presencial, Híbrido ou Remoto.',
        type: 'enum',
        required: 'no',
        rule: 'Uma das modalidades',
        values: WORK_MODE_VALUES,
        reference: { entity: 'Modalidades', list: 'work_modes' },
        example: '',
        width: 12,
      },
      {
        key: 'topLevel',
        header: 'Topo da estrutura',
        description: 'Sim = passa a não ter gestor imediato; Não = deixa de ser topo.',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: '',
        width: 10,
      },
      {
        key: 'status',
        header: 'Situação funcional',
        description: 'Ativo, Desligado ou Encerrado. Vazio = mantém.',
        type: 'enum',
        required: 'no',
        rule: 'Ativo, Desligado ou Encerrado',
        values: EMPLOYMENT_STATUS_VALUES,
        reference: { entity: 'Situações funcionais', list: 'employment_statuses' },
        example: '',
        width: 12,
      },
      {
        key: 'reason',
        header: 'Motivo',
        description: 'Motivo administrativo (sem dados de saúde).',
        type: 'text',
        max: 900,
        required: 'no',
        rule: 'Até 900 caracteres',
        example: 'Promoção fictícia',
        width: 24,
      },
    ],
  },
  {
    id: 'allocations',
    group: 'people',
    order: 12,
    version: 1,
    title: 'Rateios de Colaboradores',
    identity:
      'Colaborador + início da vigência (cada data é uma composição; uma linha por centro de custo)',
    dependsOn: ['employees', 'cost_centers'],
    prerequisites: [],
    applyPermissions: ['people.allocation.manage'],
    readPermissions: PEOPLE_READ,
    transaction: 'atomic',
    updateRules: [
      'Cada composição é o conjunto das linhas do mesmo colaborador com o mesmo início de vigência; os percentuais precisam somar exatamente 100% (até 4 casas).',
      'Composição que não soma 100% não é descartada em silêncio: todas as linhas dela aparecem como inconsistência ("rateio incompleto") e nada da composição é gravado. Corrija os percentuais ou complete os centros de custo.',
      'Nova composição encerra a anterior em aberto na véspera. Não entra composição antes de uma já registrada (o histórico não muda).',
      'A mesma composição de novo aparece como "sem alteração"; na mesma data com outros percentuais, é recusada (cancele pela tela antes).',
      'O rateio fica dentro das datas do vínculo. Colaboradores e centros de custo não são criados pela importação.',
      'Rateio entre empresas do grupo é permitido: informe a "Empresa do centro de custo" quando o centro for de outra empresa (a empresa beneficiária). A empresa do vínculo do colaborador não muda; a empresa pagadora não é dado do rateio.',
      'O centro de custo precisa estar ativo e vigente no período do rateio: começar até o início da composição e, se tiver fim de vigência, a composição precisa terminar até essa data ("Término da vigência"). Quem importa precisa ver a empresa do centro.',
    ],
    columns: [
      ...employeeRef(true),
      {
        key: 'validFrom',
        header: 'Início da vigência',
        description: 'Início da composição.',
        type: 'date',
        required: 'always',
        rule: 'Data (dd/mm/aaaa)',
        example: '01/01/2026',
        width: 13,
      },
      {
        key: 'validUntil',
        header: 'Término da vigência',
        description: 'Término da composição (opcional; o mesmo em todas as linhas da composição).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '',
        width: 13,
      },
      {
        key: 'costCenter',
        header: 'Centro de custo (código)',
        description: 'Centro de custo da linha (ativo).',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de um centro de custo ativo',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '990101001',
        width: 16,
      },
      {
        key: 'costCenterEntity',
        header: 'Empresa do centro de custo (código)',
        description: 'Empresa do centro de custo, se for outra que a do colaborador.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de empresas cadastrado(a)',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: '',
        width: 14,
      },
      {
        key: 'percentage',
        header: 'Percentual',
        description: 'Percentual do centro de custo na composição.',
        type: 'decimal',
        required: 'always',
        rule: 'Maior que 0 e até 100, com até 4 casas decimais',
        example: '75',
        width: 10,
      },
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observação administrativa da composição.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Até 1000 caracteres',
        example: '',
        width: 24,
      },
    ],
  },
  {
    id: 'dp_complement',
    group: 'people',
    order: 13,
    version: 1,
    title: 'Complementação Cadastral de DP',
    identity: 'Colaborador (ID interno ou empresa + matrícula): só atualiza; nunca cria pessoas',
    dependsOn: ['employees'],
    prerequisites: [],
    applyPermissions: ['people.private.manage', 'masterdata.import.restricted'],
    readPermissions: ['people.private.manage'],
    transaction: 'atomic',
    updateRules: [
      'Só atualiza colaboradores já identificados (ID interno ou empresa + matrícula). Nenhuma pessoa é criada nem identificada pelo nome.',
      'Dados pessoais: ' +
        RESTRICTED_RULE +
        ' Os valores nunca voltam na prévia, no relatório de erros nem no template de atualização (que traz só os identificadores e os dados trabalhistas).',
      'Dados trabalhistas (admissão, tipo de vínculo, jornada...) exigem também a administração de colaboradores.',
      'Célula vazia mantém o valor atual.',
    ],
    columns: [
      ...employeeRef(true),
      {
        key: 'nameCheck',
        header: 'Nome (conferência)',
        description: 'Só para conferência visual: não é gravado.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 28,
      },
      {
        key: 'cpf',
        header: 'CPF',
        description: 'CPF (guardado cifrado).',
        type: 'code',
        max: 14,
        required: 'no',
        rule: '11 números, dígitos verificadores válidos',
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 16,
      },
      {
        key: 'birthDate',
        header: 'Data de nascimento',
        description: 'Data de nascimento.',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa), não futura',
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 13,
      },
      {
        key: 'nationality',
        header: 'Nacionalidade',
        description: 'País (2 letras, ex.: BR).',
        type: 'code',
        max: 2,
        required: 'no',
        rule: 'Código do país com 2 letras',
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 10,
      },
      {
        key: 'foreignIdType',
        header: 'Documento estrangeiro: tipo',
        description: 'Passaporte, RNM ou Outro.',
        type: 'enum',
        required: 'no',
        rule: 'Com o número, juntos',
        values: FOREIGN_ID_VALUES,
        reference: { entity: 'Documentos estrangeiros', list: 'foreign_id_types' },
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 14,
      },
      {
        key: 'foreignIdNumber',
        header: 'Documento estrangeiro: número',
        description: 'Número do documento estrangeiro.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Até 40 caracteres',
        example: '',
        width: 16,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'maritalStatus',
        header: 'Estado civil',
        description: 'Estado civil.',
        type: 'enum',
        required: 'no',
        rule: 'Um dos valores da lista',
        values: MARITAL_VALUES,
        reference: { entity: 'Estados civis', list: 'marital_statuses' },
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 14,
      },
      {
        key: 'personalEmail',
        header: 'E-mail pessoal',
        description: 'E-mail pessoal.',
        type: 'email',
        max: 254,
        required: 'no',
        rule: 'E-mail válido',
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 26,
      },
      {
        key: 'mobilePhone',
        header: 'Celular',
        description: 'Celular (contato pessoal). Sem "+", fica sem país confirmado.',
        type: 'text',
        max: 25,
        required: 'no',
        rule: 'Até 25 caracteres. Com o código do país: +55 31 90000-0000',
        example: '',
        width: 16,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'altPhone',
        header: 'Telefone alternativo',
        description: 'Outro telefone. Sem "+", fica sem país confirmado.',
        type: 'text',
        max: 25,
        required: 'no',
        rule: 'Até 25 caracteres. Com o código do país: +55 31 90000-0000',
        example: '',
        width: 16,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'postalCode',
        header: 'CEP',
        description: 'CEP (Brasil: 8 números).',
        type: 'text',
        max: 12,
        required: 'no',
        rule: 'Até 12 caracteres',
        example: '',
        width: 11,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'street',
        header: 'Logradouro',
        description: 'Rua, avenida...',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 26,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'streetNumber',
        header: 'Número',
        description: 'Número do endereço.',
        type: 'text',
        max: 20,
        required: 'no',
        rule: 'Até 20 caracteres',
        example: '',
        width: 8,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'complement',
        header: 'Complemento',
        description: 'Complemento.',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Até 100 caracteres',
        example: '',
        width: 14,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'district',
        header: 'Bairro',
        description: 'Bairro.',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Até 100 caracteres',
        example: '',
        width: 16,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'city',
        header: 'Cidade',
        description: 'Cidade.',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Até 100 caracteres',
        example: '',
        width: 16,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'state',
        header: 'UF',
        description: 'Estado (UF).',
        type: 'text',
        max: 50,
        required: 'no',
        rule: 'Até 50 caracteres',
        example: '',
        width: 6,
        permission: 'people.private.manage',
        restricted: true,
      },
      {
        key: 'country',
        header: 'País',
        description: 'País do endereço (2 letras).',
        type: 'code',
        max: 2,
        required: 'no',
        rule: 'Código do país com 2 letras',
        permission: 'people.private.manage',
        restricted: true,
        example: '',
        width: 7,
      },
      {
        key: 'startDate',
        header: 'Data de admissão',
        description: 'Admissão (exige a administração de colaboradores).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa)',
        example: '',
        width: 13,
      },
      {
        key: 'employmentType',
        header: 'Tipo de vínculo (código)',
        description: 'Tipo de vínculo.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de tipos de vínculo cadastrado(a)',
        reference: { entity: 'Tipos de vínculo', list: 'employment_types' },
        example: '',
        width: 14,
      },
      {
        key: 'workday',
        header: 'Jornada',
        description: 'Jornada contratual.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 22,
      },
      {
        key: 'weeklyHours',
        header: 'Carga horária semanal',
        description: 'Horas por semana.',
        type: 'decimal',
        required: 'no',
        rule: 'Número maior que 0 e até 168',
        example: '',
        width: 10,
      },
      {
        key: 'unionName',
        header: 'Sindicato',
        description: 'Sindicato.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres',
        example: '',
        width: 18,
      },
      {
        key: 'collectiveAgreement',
        header: 'Convenção coletiva',
        description: 'Convenção coletiva.',
        type: 'text',
        max: 300,
        required: 'no',
        rule: 'Até 300 caracteres',
        example: '',
        width: 18,
      },
    ],
  },
  {
    id: 'management_accounts',
    group: 'finance',
    order: 14,
    version: 1,
    title: 'Plano de Contas Gerencial',
    identity: 'Código da conta gerencial',
    dependsOn: [],
    prerequisites: [],
    applyPermissions: STRUCTURE_WRITE,
    readPermissions: ['structure.catalog.read'],
    transaction: 'atomic',
    updateRules: [
      ...COMMON_UPDATE_RULES,
      'Grava no plano gerencial usado pelo FP&A (core.management_account), com as mesmas regras da tela: a conta superior é sintética, ativa e da mesma natureza; sem ciclos; conta com contas abaixo continua sintética e não muda de natureza.',
      'A conta superior pode estar no mesmo arquivo (as superiores são gravadas antes).',
      'Este template não liga contas contábeis: a correspondência contábil → gerencial será um cadastro próprio.',
    ],
    columns: [
      {
        key: 'code',
        header: 'Código',
        description: 'Código da conta gerencial. Não muda depois de gravado.',
        type: 'code',
        max: 40,
        required: 'always',
        rule: 'Letras, números, ponto, hífen ou _ (até 40); único',
        key_part: true,
        example: '9.01',
        width: 14,
      },
      {
        key: 'namePt',
        header: 'Nome (pt-BR)',
        description: 'Nome da conta em português.',
        type: 'text',
        max: 200,
        required: 'create',
        rule: 'Até 200 caracteres',
        example: 'Receita Fictícia',
        width: 32,
      },
      {
        key: 'nameEn',
        header: 'Nome (en)',
        description: 'Nome da conta em inglês.',
        type: 'text',
        max: 200,
        required: 'create',
        rule: 'Até 200 caracteres',
        example: 'Fictitious Revenue',
        width: 32,
      },
      { ...description(1000) },
      {
        key: 'parent',
        header: 'Conta superior (código)',
        description:
          'Código da conta superior (sintética, ativa e da mesma natureza). Pode estar no mesmo arquivo.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Código de uma conta gerencial sintética',
        reference: { entity: 'Plano de Contas Gerencial', list: 'management_accounts' },
        example: '9',
        width: 16,
      },
      {
        key: 'synthetic',
        header: 'Sintética',
        description: 'Sim = agrupa outras contas; Não = analítica (recebe valores).',
        type: 'enum',
        required: 'create',
        rule: 'Sim ou Não',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: 'Não',
        width: 10,
      },
      {
        key: 'nature',
        header: 'Natureza',
        description: 'Natureza gerencial (DRE).',
        type: 'enum',
        required: 'create',
        rule: 'Uma das naturezas da lista',
        values: MANAGEMENT_NATURE_VALUES,
        reference: { entity: 'Naturezas', list: 'account_natures' },
        example: 'Receita',
        width: 22,
      },
      {
        key: 'sortOrder',
        header: 'Ordem',
        description: 'Ordem de apresentação entre as contas do mesmo nível.',
        type: 'integer',
        required: 'no',
        rule: 'Número inteiro de 1 a 99999 (vazio ao incluir: 100)',
        example: '10',
        width: 8,
      },
      status(),
    ],
  },
  {
    id: 'accounting_accounts',
    group: 'finance',
    order: 15,
    version: 1,
    title: 'Plano de Contas Contábil',
    identity:
      'ID interno (template de atualização); sem ele, ID NetSuite; sem os dois, o par número + nome (exato)',
    dependsOn: [],
    prerequisites: ['Moedas'],
    applyPermissions: STRUCTURE_WRITE,
    readPermissions: ['structure.catalog.read'],
    transaction: 'atomic',
    updateRules: [
      'Identificação, nesta ordem: ID interno (vem no template de atualização); ID NetSuite (Internal ID, imutável na origem); sem os dois, o par exato número + nome. Nome sozinho nunca identifica a conta.',
      'Conciliação controlada: sem ID, se já existir conta com o mesmo número ou o mesmo nome, mas não os dois, a linha fica "para conciliar" (use o template de atualização ou informe o ID NetSuite). Nada é associado por semelhança de nomes.',
      'Célula vazia em campo opcional mantém o valor atual. Nada é excluído nem inativado por estar fora da planilha; a coluna "Excluir" só aceita "Não".',
      '"Inativo" é guardado como veio do NetSuite (dado de origem). A situação no BackOffice segue a coluna "Situação no BackOffice"; vazia, vale o "Inativo" para contas analíticas. Conta resumo marcada como inativa na exportação NÃO é presumida inativa: fica ativa como agrupadora, com aviso, até a Controladoria decidir.',
      'O número é único entre as contas ativas (o NetSuite guarda contas inativas com o mesmo número). O número de uma conta existente não muda pela importação.',
      'A conta superior é informada pelo ID NetSuite ou pelo número (nunca deduzida do nome ou do próprio número). Pode estar no mesmo arquivo. Se o número da conta não começar pelo da superior, a linha recebe um aviso.',
      'Primeira carga: contas analíticas sem superior são aceitas. A importação não cria contas sintéticas sozinha nem monta a hierarquia pelos prefixos do número ou pela semelhança dos nomes. Uma conta não resumo cujo número é o início do número de outras do arquivo recebe só um aviso, para conferência.',
      'Cargas seguintes: incluir contas sintéticas, ligar as superiores, completar moeda e descrição, informar o ID NetSuite e incluir contas que ficaram de fora, sem duplicar (a conta é reconhecida pelo ID interno, pelo ID NetSuite ou pelo par número + nome) e sem mudar o ID interno.',
      'Número guardado como texto, como veio (zeros à esquerda e pontos mantidos).',
      'Moeda e descrição são opcionais: vazias ficam vazias (nenhuma moeda é preenchida por padrão). Moeda pelo código (BRL, USD...); os nomes da exportação do NetSuite REAL, US DOLLAR e EURO são convertidos para BRL, USD e EUR, com aviso.',
      'Plano contábil, distinto do gerencial do FP&A: não liga contas gerenciais.',
    ],
    columns: [
      {
        key: 'id',
        header: 'ID interno',
        description:
          'Identificador do sistema (vem no template de atualização). Vazio = conta identificada pelo ID NetSuite ou pelo número + nome.',
        type: 'code',
        max: 36,
        required: 'no',
        rule: 'Não altere: identifica a conta existente',
        example: '',
        width: 38,
      },
      {
        key: 'netsuiteId',
        header: 'ID NetSuite',
        description:
          'Internal ID da conta no NetSuite, quando houver (identificador imutável da origem). Opcional: a primeira carga não precisa dele.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Letras, números, hífen ou _ (até 40); único',
        example: '',
        width: 12,
      },
      {
        key: 'code',
        header: 'Número',
        description:
          'Número da conta (ex.: 1.1.01.002.001), guardado como texto e como veio. Opcional (contas de sistema não têm).',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Números, letras, ponto ou hífen (até 40); único entre as ativas',
        example: '9.1.01.001',
        width: 18,
      },
      {
        key: 'name',
        header: 'Conta',
        description: 'Nome da conta. Único.',
        type: 'text',
        max: 200,
        required: 'always',
        rule: 'Até 200 caracteres; único',
        key_part: true,
        example: 'CONTA FICTÍCIA',
        width: 36,
      },
      {
        key: 'accountType',
        header: 'Tipo',
        description: 'Tipo contábil (como no NetSuite).',
        type: 'enum',
        required: 'create',
        rule: 'Um dos tipos da lista',
        values: ACCOUNTING_TYPE_VALUES,
        reference: { entity: 'Tipos de conta', list: 'account_types' },
        example: 'Despesa',
        width: 26,
      },
      { ...description(1000) },
      {
        key: 'currency',
        header: 'Moeda',
        description: 'Moeda da conta (código de 3 letras), quando houver. Vazia fica vazia.',
        type: 'reference',
        max: 20,
        required: 'no',
        rule: 'Código da moeda (ex.: BRL); REAL, US DOLLAR e EURO são convertidos',
        reference: { entity: 'Moedas', list: 'currencies' },
        example: 'BRL',
        width: 8,
      },
      {
        key: 'summary',
        header: 'Resumo',
        description: 'Sim = conta resumo (sintética, agrupa outras); Não = analítica.',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não (vazio ao incluir: Não)',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: 'Não',
        width: 9,
      },
      {
        key: 'inactive',
        header: 'Inativo',
        description:
          'Sim = conta inativa no NetSuite (como na exportação). Guardado como dado de origem.',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não (vazio ao incluir: Não)',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: 'Não',
        width: 9,
      },
      {
        key: 'situation',
        header: 'Situação no BackOffice',
        description:
          'Ativa ou Inativa no BackOffice. Vazia: contas analíticas seguem o "Inativo"; contas resumo ficam ativas (com aviso).',
        type: 'enum',
        required: 'no',
        rule: 'Ativa ou Inativa (ou vazio)',
        values: [
          { label: 'Ativa', value: 'active' },
          { label: 'Inativa', value: 'inactive' },
        ],
        reference: { entity: 'Situação', list: 'status' },
        example: '',
        width: 12,
      },
      {
        key: 'exclude',
        header: 'Excluir',
        description:
          'Coluna da exportação do NetSuite. A importação nunca exclui contas: só "Não" é aceito.',
        type: 'enum',
        required: 'no',
        rule: 'Não (ou vazio)',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: 'Não',
        width: 9,
      },
      {
        key: 'parent',
        header: 'Conta superior (número)',
        description:
          'Número da conta superior, quando houver (conta analítica sem superior é aceita). Pode estar no mesmo arquivo.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'Número de uma conta (ativa) existente ou do arquivo',
        reference: { entity: 'Plano de Contas Contábil', list: 'accounting_accounts' },
        example: '9.1.01',
        width: 18,
      },
      {
        key: 'parentNetsuiteId',
        header: 'Conta superior (ID NetSuite)',
        description:
          'Internal ID da conta superior (alternativa ao número; vale primeiro). Pode estar no mesmo arquivo.',
        type: 'code',
        max: 40,
        required: 'no',
        rule: 'ID NetSuite de uma conta existente ou do arquivo',
        example: '',
        width: 14,
      },
    ],
  },
  // ===================================================================
  // Relatórios da ADP (ADR 0091): a folha, o ponto e a admissão são feitos na ADP; aqui entram
  // as admissões, os desligamentos e o custo da folha por centro e mês, para manter o cadastro e o
  // FP&A em dia. O layout segue a planilha exportada da ADP (padrão provisório até a amostra real).
  // ===================================================================
  {
    id: 'adp_movements',
    group: 'people',
    order: 16,
    version: 1,
    title: 'Admissões e Desligamentos (ADP)',
    identity: 'Empresa + matrícula + evento + data',
    dependsOn: ['persons', 'employees', 'movements'],
    prerequisites: ['Empresas', 'Cargos', 'Centros de custo'],
    applyPermissions: ['people.person.manage', 'people.employee.manage'],
    readPermissions: PEOPLE_READ,
    transaction: 'atomic',
    updateRules: [
      'Cada linha é um evento da ADP: Admissão ou Desligamento, com a data. A empresa e a matrícula identificam o vínculo; o nome nunca identifica ninguém.',
      'Admissão: a pessoa é achada pelo e-mail corporativo; sem cadastro, é criada (nome parecido com alguém já cadastrado fica "para conciliar" até escrever Sim em "Confirmar pessoa nova"). O vínculo é criado com a data de admissão e a situação inicial Ativo. Sem e-mail corporativo, a linha é recusada (cadastre a pessoa em Pessoas antes).',
      'A mesma admissão de novo (mesma empresa, matrícula e data) aparece como "sem alteração". Matrícula já cadastrada com outra data é recusada.',
      'Desligamento: grava a situação Desligado a partir da data, encerra os rateios em aberto, corta o acesso ao sistema (ADR 0086) e abre o checklist de desligamento da TI (ADR 0085). Não entram datas anteriores à última movimentação registrada.',
      'Não cria usuário de acesso nem concede perfis: o acesso é dado pela tela "Dar acesso em lote".',
    ],
    columns: [
      {
        key: 'event',
        header: 'Evento',
        description: 'Admissão ou Desligamento.',
        type: 'enum',
        required: 'always',
        rule: 'Admissão ou Desligamento',
        key_part: true,
        values: ADP_EVENT_VALUES,
        example: 'Admissão',
        width: 14,
      },
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description: 'Empresa do vínculo (com a matrícula, identifica o colaborador).',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa onde você administra colaboradores',
        key_part: true,
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'registration',
        header: 'Matrícula',
        description: 'Matrícula do vínculo na empresa (a mesma da ADP).',
        type: 'text',
        max: 30,
        required: 'always',
        rule: 'Letras, números, ponto, barra ou hífen (até 30)',
        key_part: true,
        example: '9001',
        width: 12,
      },
      {
        key: 'date',
        header: 'Data do evento',
        description: 'Data de admissão ou de desligamento.',
        type: 'date',
        required: 'always',
        rule: 'Data (dd/mm/aaaa)',
        key_part: true,
        example: '01/03/2026',
        width: 13,
      },
      {
        key: 'fullName',
        header: 'Nome completo',
        description:
          'Nome da pessoa (só na admissão de quem ainda não tem cadastro). Não identifica ninguém.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Obrigatório na admissão de pessoa nova. Até 200 caracteres',
        example: 'Pessoa Fictícia',
        width: 28,
      },
      {
        key: 'corporateEmail',
        header: 'E-mail corporativo',
        description: 'E-mail da empresa: acha a pessoa já cadastrada ou entra no cadastro novo.',
        type: 'email',
        max: 200,
        required: 'no',
        rule: 'Obrigatório na admissão. E-mail válido',
        example: 'pessoa.ficticia@exemplo.com',
        width: 28,
      },
      {
        key: 'confirmNew',
        header: 'Confirmar pessoa nova',
        description: 'Sim quando há alguém com nome parecido e é mesmo outra pessoa.',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        example: '',
        width: 12,
      },
      {
        key: 'employmentType',
        header: 'Tipo de vínculo (código)',
        description: 'Tipo de vínculo na admissão (CLT, estágio...).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de tipos de vínculo cadastrado(a)',
        reference: { entity: 'Tipos de vínculo', list: 'employment_types' },
        example: '',
        width: 14,
      },
      {
        key: 'job',
        header: 'Cargo (código)',
        description: 'Cargo na admissão.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de cargos cadastrado(a)',
        reference: { entity: 'Cargos', list: 'jobs' },
        example: '999001',
        width: 14,
      },
      {
        key: 'costCenter',
        header: 'Centro de custo (código)',
        description: 'Centro de custo principal na admissão (ativo, da empresa).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de centros de custo cadastrado(a)',
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '',
        width: 16,
      },
      {
        key: 'branch',
        header: 'Filial (código)',
        description: 'Filial da empresa na admissão.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de filiais cadastrado(a)',
        reference: { entity: 'Filiais', list: 'branches' },
        example: '',
        width: 14,
      },
      {
        key: 'department',
        header: 'Departamento (código)',
        description: 'Departamento na admissão.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de departamentos cadastrado(a)',
        reference: { entity: 'Departamentos', list: 'departments' },
        example: '',
        width: 16,
      },
      {
        key: 'reason',
        header: 'Motivo',
        description: 'Motivo do desligamento (vai para o histórico).',
        type: 'text',
        max: 500,
        required: 'no',
        rule: 'Até 500 caracteres',
        example: 'Pedido de demissão',
        width: 24,
      },
    ],
  },
  {
    id: 'company_bank_accounts',
    group: 'finance',
    order: 21,
    version: 2,
    title: 'Contas Bancárias das Empresas',
    identity:
      'A própria conta: banco + agência + número (Brasil), routing + número (EUA), SWIFT + número (outro país) ou IBAN',
    dependsOn: [],
    prerequisites: ['Empresas', 'Moedas'],
    applyPermissions: ['finance.bank_account.manage'],
    readPermissions: ['finance.bank_account.read'],
    transaction: 'atomic',
    updateRules: [
      'Só inclui contas novas. Conta já cadastrada no grupo (em qualquer empresa) é recusada como repetida; para alterar uma conta, use Alterar conta na tela (versão nova, com confirmação).',
      'Toda conta importada entra "Aguardando confirmação": só passa a valer depois que outra pessoa, com a permissão de confirmar contas da empresa, abre a conta e confirma. Nada é confirmado pela importação.',
      'O número da conta e o IBAN são gravados protegidos e só aparecem mascarados; nunca voltam em template, prévia, histórico ou arquivo de inconsistências.',
      'Conta no Brasil: o banco é o código de 3 dígitos da lista do Banco Central (aba Listas de Referência); o nome e o ISPB vêm da lista. Agência, número e modalidade são obrigatórios.',
      'Conta nos Estados Unidos: routing number (9 dígitos), número e modalidade. Conta com IBAN: o IBAN (o país vem dele). Outro país: país, SWIFT e número; no Reino Unido, na Austrália, na Índia e no Canadá, também o código local do banco.',
      'Chave PIX: só em conta no Brasil, sempre com o tipo da chave.',
      'Fora do Brasil, o nome do banco é obrigatório; endereço do banco e banco intermediário (SWIFT e nome juntos; a conta no intermediário é opcional) são opcionais.',
      'Motivo vazio: "Carga pela Central de Importações".',
    ],
    columns: [
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description: 'Empresa dona da conta.',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa em que você cadastra contas',
        key_part: true,
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'nickname',
        header: 'Apelido',
        description: 'Nome curto para reconhecer a conta (ex.: "Itaú operacional").',
        type: 'text',
        max: 80,
        required: 'always',
        rule: 'De 2 a 80 caracteres',
        example: 'Banco Fictício operacional',
        width: 24,
      },
      {
        key: 'kind',
        header: 'Tipo de conta',
        description: 'Onde fica a conta.',
        type: 'enum',
        required: 'always',
        rule: 'Um dos tipos da lista',
        values: COMPANY_BANK_KIND_VALUES,
        reference: { entity: 'Tipos de conta bancária', list: 'bank_account_kinds' },
        example: 'Conta no Brasil',
        width: 22,
      },
      {
        key: 'currency',
        header: 'Moeda',
        description: 'Moeda da conta (código ISO).',
        type: 'reference',
        max: 3,
        required: 'always',
        rule: 'Código de uma moeda cadastrada (BRL, USD...)',
        reference: { entity: 'Moedas', list: 'currencies' },
        example: 'BRL',
        width: 8,
      },
      {
        key: 'bankCode',
        header: 'Banco (código)',
        description: 'Conta no Brasil: código de 3 dígitos do banco na lista do Banco Central.',
        type: 'reference',
        max: 3,
        required: 'no',
        rule: '3 dígitos da lista (obrigatório na conta no Brasil)',
        reference: { entity: 'Bancos do Brasil', list: 'banks' },
        example: '341',
        width: 10,
      },
      {
        key: 'bankName',
        header: 'Nome do banco',
        description: 'Conta fora do Brasil: nome do banco. No Brasil, vem da lista.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres (obrigatório fora do Brasil)',
        example: '',
        width: 24,
      },
      {
        key: 'branch',
        header: 'Agência',
        description: 'Conta no Brasil: agência, com o dígito se houver.',
        type: 'text',
        max: 7,
        required: 'no',
        rule: 'Até 5 números, com dígito opcional (ex.: 0001 ou 0001-9)',
        example: '0001',
        width: 10,
      },
      {
        key: 'accountNumber',
        header: 'Número da conta',
        description: 'Número da conta com o dígito (Brasil, EUA e outro país). Gravado protegido.',
        type: 'text',
        max: 34,
        required: 'no',
        rule: 'Números (e letras fora do Brasil), com o dígito',
        restricted: true,
        example: '12345-6',
        width: 18,
      },
      {
        key: 'iban',
        header: 'IBAN',
        description: 'Conta com IBAN: o IBAN completo. Gravado protegido.',
        type: 'text',
        max: 42,
        required: 'no',
        rule: 'IBAN válido (com o dígito de controle)',
        restricted: true,
        example: '',
        width: 30,
      },
      {
        key: 'accountType',
        header: 'Modalidade',
        description: 'Tipo da conta no banco (Brasil e EUA: obrigatório).',
        type: 'enum',
        required: 'no',
        rule: 'Uma das modalidades da lista',
        values: COMPANY_BANK_ACCOUNT_TYPE_VALUES,
        reference: { entity: 'Modalidades', list: 'bank_account_types' },
        example: 'Conta corrente',
        width: 18,
      },
      {
        key: 'country',
        header: 'País (outro país)',
        description: 'Conta em outro país (SWIFT): país do banco, com 2 letras (ISO).',
        type: 'code',
        max: 2,
        required: 'no',
        rule: '2 letras (ex.: GB, JP)',
        example: '',
        width: 10,
      },
      {
        key: 'swiftBic',
        header: 'SWIFT/BIC',
        description:
          'Código SWIFT do banco (obrigatório em outro país; opcional no IBAN e nos EUA).',
        type: 'code',
        max: 11,
        required: 'no',
        rule: '8 ou 11 letras e números',
        example: '',
        width: 14,
      },
      {
        key: 'routingNumber',
        header: 'Routing number (EUA)',
        description: 'Conta nos Estados Unidos: routing number (ABA) de 9 dígitos.',
        type: 'text',
        max: 9,
        required: 'no',
        rule: '9 dígitos',
        example: '',
        width: 14,
      },
      {
        key: 'localCode',
        header: 'Código local do banco',
        description:
          'Outro país: sort code (Reino Unido), BSB (Austrália), IFSC (Índia) ou código do banco (Canadá).',
        type: 'text',
        max: 20,
        required: 'no',
        rule: 'Obrigatório no Reino Unido, na Austrália, na Índia e no Canadá',
        example: '',
        width: 14,
      },
      {
        key: 'bankAddress',
        header: 'Endereço do banco',
        description: 'Fora do Brasil: endereço do banco (opcional).',
        type: 'text',
        max: 300,
        required: 'no',
        rule: 'Até 300 caracteres',
        example: '',
        width: 28,
      },
      {
        key: 'intermediarySwift',
        header: 'SWIFT do intermediário',
        description: 'Fora do Brasil, se o pagamento passa por um banco intermediário.',
        type: 'code',
        max: 11,
        required: 'no',
        rule: '8 ou 11 letras e números; junto com o nome do intermediário',
        example: '',
        width: 14,
      },
      {
        key: 'intermediaryName',
        header: 'Nome do intermediário',
        description: 'Nome do banco intermediário.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Até 200 caracteres; junto com o SWIFT do intermediário',
        example: '',
        width: 22,
      },
      {
        key: 'intermediaryAccount',
        header: 'Conta no intermediário',
        description: 'Conta do banco da empresa no banco intermediário (opcional).',
        type: 'code',
        max: 34,
        required: 'no',
        rule: 'Até 34 letras e números',
        example: '',
        width: 18,
      },
      {
        key: 'pixKeyType',
        header: 'Tipo da chave PIX',
        description: 'Conta no Brasil: tipo da chave. Obrigatório se a chave for informada.',
        type: 'enum',
        required: 'no',
        rule: 'Um dos tipos da lista',
        values: COMPANY_BANK_PIX_TYPE_VALUES,
        reference: { entity: 'Tipos de chave PIX', list: 'pix_key_types' },
        example: '',
        width: 16,
      },
      {
        key: 'pixKey',
        header: 'Chave PIX para receber',
        description: 'Conta no Brasil: chave PIX informada a quem paga a empresa (opcional).',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Válida para o tipo escolhido',
        example: '',
        width: 22,
      },
      {
        key: 'purpose',
        header: 'Finalidade',
        description: 'Para que a conta é usada.',
        type: 'enum',
        required: 'always',
        rule: 'Uma das finalidades da lista',
        values: COMPANY_BANK_PURPOSE_VALUES,
        reference: { entity: 'Finalidades', list: 'bank_account_purposes' },
        example: 'Operacional',
        width: 16,
      },
      {
        key: 'validFrom',
        header: 'Vale a partir de',
        description: 'Início da vigência da conta.',
        type: 'date',
        required: 'always',
        rule: 'Data (dd/mm/aaaa)',
        example: '01/01/2026',
        width: 13,
      },
      {
        key: 'validUntil',
        header: 'Vale até',
        description: 'Fim da vigência (opcional).',
        type: 'date',
        required: 'no',
        rule: 'Data (dd/mm/aaaa), igual ou depois do início',
        example: '',
        width: 13,
      },
      {
        key: 'integrationChannel',
        header: 'Como os dados chegam do banco',
        description: 'Vazio = Digitação.',
        type: 'enum',
        required: 'no',
        rule: 'Um dos canais da lista',
        values: COMPANY_BANK_CHANNEL_VALUES,
        reference: { entity: 'Canais', list: 'bank_integration_channels' },
        example: 'Arquivo do banco (OFX, CNAB)',
        width: 22,
      },
      {
        key: 'changeReason',
        header: 'Motivo',
        description: 'Vai para o histórico da conta. Vazio: "Carga pela Central de Importações".',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'De 3 a 1000 caracteres',
        example: '',
        width: 24,
      },
    ],
  },
  {
    id: 'adp_payroll_cost',
    group: 'people',
    order: 17,
    version: 1,
    title: 'Custo da Folha (ADP)',
    identity: 'Empresa + centro de custo + mês + moeda',
    dependsOn: ['cost_centers'],
    prerequisites: ['Empresas', 'Moedas'],
    applyPermissions: ['people.payroll_cost.manage', 'masterdata.import.restricted'],
    readPermissions: ['people.payroll_cost.manage'],
    transaction: 'atomic',
    updateRules: [
      'Só totais por empresa, centro de custo, mês e moeda: nenhum valor por pessoa.',
      'Valores salariais: exigem o custo da folha e importar dados restritos, e ficam bloqueados nesta instalação até a liberação dos valores salariais. Os valores nunca voltam no template de atualização.',
      'O mês é gravado pelo primeiro dia (qualquer data do mês serve).',
      'O mesmo valor de novo aparece como "sem alteração". Valor diferente para um mês já importado é uma correção: precisa do motivo e grava uma linha nova (a anterior fica no histórico).',
      'O FP&A consulta esses totais (sigilo de pessoal); nada é gravado no orçamento.',
    ],
    columns: [
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description: 'Empresa da folha.',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa cadastrada',
        key_part: true,
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'costCenter',
        header: 'Centro de custo (código)',
        description: 'Centro de custo da empresa.',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de centros de custo cadastrado(a)',
        key_part: true,
        reference: { entity: 'Centros de custo', list: 'cost_centers' },
        example: '',
        width: 16,
      },
      {
        key: 'month',
        header: 'Mês',
        description: 'Mês de competência da folha.',
        type: 'date',
        required: 'always',
        rule: 'Data (dd/mm/aaaa) de qualquer dia do mês',
        key_part: true,
        example: '01/03/2026',
        width: 13,
      },
      {
        key: 'currency',
        header: 'Moeda',
        description: 'Moeda dos valores (código ISO).',
        type: 'reference',
        max: 3,
        required: 'always',
        rule: 'Código de uma moeda cadastrada (BRL, USD...)',
        key_part: true,
        reference: { entity: 'Moedas', list: 'currencies' },
        example: 'BRL',
        width: 8,
      },
      {
        key: 'amount',
        header: 'Custo total',
        description:
          'Custo total da folha do centro no mês (salários, encargos e benefícios, como no relatório da ADP).',
        type: 'decimal',
        required: 'always',
        rule: 'Número com até 4 casas decimais, zero ou positivo',
        permission: 'people.payroll_cost.manage',
        restricted: true,
        example: '12345,67',
        width: 16,
      },
      {
        key: 'headcount',
        header: 'Quantidade de pessoas',
        description: 'Pessoas na folha do centro no mês (opcional).',
        type: 'integer',
        required: 'no',
        rule: 'Número inteiro, zero ou positivo',
        example: '4',
        width: 12,
      },
      {
        key: 'branch',
        header: 'Filial (código)',
        description: 'Filial da empresa (opcional).',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de filiais cadastrado(a)',
        reference: { entity: 'Filiais', list: 'branches' },
        example: '',
        width: 14,
      },
      {
        key: 'reason',
        header: 'Motivo da correção',
        description: 'Obrigatório quando o mês já foi importado com outro valor.',
        type: 'text',
        max: 500,
        required: 'no',
        rule: 'De 3 a 500 caracteres',
        example: '',
        width: 24,
      },
    ],
  },
  {
    id: 'partners',
    group: 'partners',
    order: 18,
    version: 1,
    title: 'Fornecedores e Parceiros',
    identity: 'País + tipo de documento + documento (sem documento: o identificador alternativo)',
    dependsOn: [],
    prerequisites: ['Empresas', 'Categorias de parceiro (se usadas)'],
    applyPermissions: PARTNER_WRITE,
    readPermissions: PARTNER_READ,
    transaction: 'atomic',
    updateRules: [
      'Grava no cadastro único de parceiros (o mesmo da tela de Fornecedores), com as mesmas regras de documento, origem (nacional ou internacional) e duplicidade.',
      'A importação só inclui: fornecedor que já existe (mesmo documento, ou o mesmo identificador alternativo sem documento) sai "sem alteração", com aviso dos campos diferentes. Alterações são feitas na ficha.',
      'Cada fornecedor novo nasce com o vínculo pendente na empresa da linha, onde você precisa poder cadastrar parceiros. A homologação continua na ficha, por outra pessoa: quem cadastra não homologa.',
      'Para ligar o fornecedor a outras empresas, use o modelo Vínculos de Fornecedores com Empresas.',
      'Fornecedor inativo não é reativado pela importação: a linha é recusada.',
      'Sem documento: explique o motivo e informe o identificador alternativo (registro comercial, site ou link da loja). Nome parecido com outro do mesmo país só passa com a justificativa de duplicidade.',
      'A situação do CNPJ na Receita não é consultada pela importação: confira na ficha (Conferir na Receita).',
      'Ficam fora: dados bancários, condições de pagamento, documentos e homologação.',
    ],
    columns: [
      {
        key: 'personType',
        header: 'Tipo de pessoa',
        description: 'PJ (empresa) ou PF (pessoa física).',
        type: 'enum',
        required: 'always',
        rule: 'PJ ou PF',
        key_part: true,
        values: PERSON_TYPE_VALUES,
        example: 'PJ',
        width: 12,
      },
      {
        key: 'country',
        header: 'País',
        description: 'País do fornecedor, em 2 letras (ISO 3166).',
        type: 'code',
        max: 2,
        required: 'always',
        rule: '2 letras (ex.: BR, US)',
        key_part: true,
        example: 'BR',
        width: 8,
      },
      {
        key: 'taxIdType',
        header: 'Tipo de documento',
        description:
          'CNPJ ou CPF (só no Brasil), Estrangeiro (documento de outro país) ou Sem documento.',
        type: 'enum',
        required: 'always',
        rule: 'CNPJ, CPF, Estrangeiro ou Sem documento',
        key_part: true,
        values: TAX_ID_TYPE_VALUES,
        example: 'CNPJ',
        width: 16,
      },
      {
        key: 'taxId',
        header: 'Documento',
        description:
          'CNPJ, CPF ou documento estrangeiro. A pontuação é retirada; o dígito do CPF e do CNPJ é conferido (CNPJ alfanumérico aceito). Vazio só em Sem documento.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Obrigatório, menos em Sem documento',
        example: '11.222.333/0001-81',
        width: 20,
      },
      {
        key: 'taxIdExceptionReason',
        header: 'Motivo sem documento',
        description: 'Só em Sem documento: por que o fornecedor não tem documento.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Em Sem documento: de 10 a 1.000 caracteres',
        width: 30,
      },
      {
        key: 'altIdentifier',
        header: 'Identificador alternativo',
        description:
          'Só em Sem documento: registro comercial, site, domínio de e-mail ou o link da loja do vendedor de marketplace.',
        type: 'text',
        max: 200,
        required: 'no',
        rule: 'Em Sem documento: obrigatório',
        width: 30,
      },
      {
        key: 'marketplaceSeller',
        header: 'Vendedor de marketplace',
        description:
          'Sim para o vendedor de marketplace ou loja física, que fica sem documento, com o link da loja no identificador alternativo, mesmo no Brasil.',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não (vazio = Não); Sim só com Sem documento',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        width: 12,
      },
      {
        key: 'legalName',
        header: 'Razão social',
        description: 'Nome jurídico (ou o nome completo da pessoa física).',
        type: 'text',
        max: 300,
        required: 'always',
        rule: 'Até 300 caracteres',
        example: 'Papelaria Fictícia Ltda',
        width: 34,
      },
      {
        key: 'tradeName',
        header: 'Nome fantasia',
        description: 'Nome comercial, se houver.',
        type: 'text',
        max: 300,
        required: 'no',
        rule: 'Até 300 caracteres',
        width: 24,
      },
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description:
          'Empresa do grupo onde o fornecedor é cadastrado (o vínculo nasce pendente de homologação).',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa ativa, onde você cadastra parceiros',
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
      {
        key: 'category',
        header: 'Categoria (código)',
        description: 'Categoria do parceiro, que decide os documentos exigidos.',
        type: 'reference',
        max: 40,
        required: 'no',
        rule: 'Código de uma categoria de parceiro ativa',
        reference: { entity: 'Categorias de parceiro', list: 'partner_categories' },
        width: 16,
      },
      ...PARTNER_ADDRESS_COLUMNS,
      {
        key: 'stateRegistration',
        header: 'Inscrição estadual',
        description: 'Só no Brasil. "ISENTO" quando não houver.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Só no Brasil',
        width: 16,
      },
      {
        key: 'municipalRegistration',
        header: 'Inscrição municipal',
        description: 'Só no Brasil. "ISENTO" quando não houver.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Só no Brasil',
        width: 16,
      },
      {
        key: 'email',
        header: 'E-mail',
        description: 'E-mail geral do fornecedor.',
        type: 'email',
        required: 'no',
        rule: 'E-mail válido',
        width: 26,
      },
      {
        key: 'phone',
        header: 'Telefone',
        description: 'Telefone geral do fornecedor.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Até 40 caracteres',
        width: 16,
      },
      {
        key: 'website',
        header: 'Site',
        description: 'Endereço do site.',
        type: 'text',
        max: 300,
        required: 'no',
        rule: 'Até 300 caracteres',
        width: 24,
      },
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observações gerais.',
        type: 'text',
        max: 4000,
        required: 'no',
        rule: 'Até 4.000 caracteres',
        width: 30,
      },
      {
        key: 'duplicateCheckNote',
        header: 'Justificativa de duplicidade',
        description:
          'Só quando há outro parceiro sem documento com nome parecido no mesmo país: por que não é o mesmo.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Exigida só quando a pré-validação avisa de nome parecido',
        width: 30,
      },
      {
        key: 'netsuiteCode',
        header: 'Código NetSuite',
        description: 'Código do fornecedor no NetSuite (só a referência, sem integração).',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Não pode ser o de outro parceiro',
        width: 16,
      },
    ],
  },
  {
    id: 'partner_links',
    group: 'partners',
    order: 19,
    version: 1,
    title: 'Vínculos de Fornecedores com Empresas',
    identity: 'Fornecedor (país + tipo de documento + documento) + empresa',
    dependsOn: ['partners'],
    prerequisites: ['Empresas'],
    applyPermissions: PARTNER_WRITE,
    readPermissions: PARTNER_READ,
    transaction: 'atomic',
    updateRules: [
      'Liga um fornecedor que já está no cadastro a outra empresa do grupo. O vínculo nasce pendente; a homologação é feita na ficha, por outra pessoa.',
      'O fornecedor é achado pelo país, tipo de documento e documento; sem documento, pelo identificador alternativo.',
      'Vínculo que já existe sai "sem alteração". Vínculo recusado volta a pendente, como o novo pedido na ficha.',
      'Você precisa poder cadastrar parceiros na empresa da linha.',
    ],
    columns: [
      ...PARTNER_KEY_COLUMNS,
      {
        key: 'legalEntity',
        header: 'Empresa (código)',
        description: 'Empresa do grupo que passa a trabalhar com o fornecedor.',
        type: 'reference',
        max: 40,
        required: 'always',
        rule: 'Código de uma empresa ativa, onde você cadastra parceiros',
        key_part: true,
        reference: { entity: 'Empresas', list: 'legal_entities' },
        example: 'FIC',
        width: 14,
      },
    ],
  },
  {
    id: 'partner_contacts',
    group: 'partners',
    order: 20,
    version: 1,
    title: 'Contatos de Fornecedores',
    identity: 'Fornecedor (país + tipo de documento + documento) + nome do contato',
    dependsOn: ['partners'],
    prerequisites: [],
    applyPermissions: PARTNER_WRITE,
    readPermissions: PARTNER_READ,
    transaction: 'atomic',
    updateRules: [
      'Inclui contatos em fornecedores que já estão no cadastro e que você vê. O fornecedor é achado pelo país, tipo de documento e documento; sem documento, pelo identificador alternativo.',
      'Contato com o mesmo nome no mesmo fornecedor sai "sem alteração": alterações são feitas na ficha.',
      'Principal = Sim troca o contato principal do fornecedor. No arquivo, só um contato principal por fornecedor.',
    ],
    columns: [
      ...PARTNER_KEY_COLUMNS,
      {
        key: 'name',
        header: 'Nome',
        description: 'Nome do contato.',
        type: 'text',
        max: 200,
        required: 'always',
        rule: 'Até 200 caracteres',
        key_part: true,
        example: 'Ana Exemplo',
        width: 26,
      },
      {
        key: 'role',
        header: 'Cargo',
        description: 'Cargo ou área do contato.',
        type: 'text',
        max: 100,
        required: 'no',
        rule: 'Até 100 caracteres',
        width: 18,
      },
      {
        key: 'email',
        header: 'E-mail',
        description: 'E-mail do contato.',
        type: 'email',
        required: 'no',
        rule: 'E-mail válido',
        width: 26,
      },
      {
        key: 'phone',
        header: 'Telefone',
        description: 'Telefone do contato.',
        type: 'text',
        max: 40,
        required: 'no',
        rule: 'Até 40 caracteres',
        width: 16,
      },
      {
        key: 'isPrimary',
        header: 'Principal',
        description: 'Sim para o contato principal do fornecedor (troca o atual).',
        type: 'enum',
        required: 'no',
        rule: 'Sim ou Não (vazio = Não); um por fornecedor no arquivo',
        values: YES_NO,
        reference: { entity: 'Sim/Não', list: 'yes_no' },
        width: 10,
      },
      {
        key: 'notes',
        header: 'Observações',
        description: 'Observações sobre o contato.',
        type: 'text',
        max: 1000,
        required: 'no',
        rule: 'Até 1.000 caracteres',
        width: 30,
      },
    ],
  },
];

export function importKind(id: string): ImportKind | undefined {
  return IMPORT_KINDS.find((k) => k.id === id);
}

/** Marcador da aba Instruções (célula A2): confere o cadastro e a versão do template enviado. */
export function templateMarker(kind: Pick<ImportKind, 'id' | 'version'>): string {
  return `LOOT-DM:${kind.id}:v${kind.version}`;
}

export const IMPORT_SHEETS = {
  data: 'Dados',
  instructions: 'Instruções',
  dictionary: 'Dicionário de Dados',
  lists: 'Listas de Referência',
} as const;

// ----- Entradas da API -----

const fileName = z.string().trim().min(1).max(200);
/** Base64 do arquivo (até IMPORT_MAX_FILE_BYTES). */
const contentBase64 = z
  .string()
  .min(1)
  .max(Math.ceil((IMPORT_MAX_FILE_BYTES * 4) / 3) + 4);

export const importValidateInput = z.object({
  fileName,
  contentBase64,
  options: z
    .object({
      defaultValidFrom: z.iso
        .date()
        .or(z.literal('').transform(() => null))
        .nullable()
        .optional(),
    })
    .default({}),
});
export type ImportValidateInput = z.infer<typeof importValidateInput>;

export const importApplyInput = z.object({
  fileName,
  contentBase64,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  confirm: z.literal(true),
});
export type ImportApplyInput = z.infer<typeof importApplyInput>;

export const importHistoryQuery = z.object({
  kind: z.enum(IMPORT_KIND_IDS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
